import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { Leaf, Droplets, ThermometerSun, CheckCircle, Heart, ChefHat, Flame, ShoppingCart, Loader2, MessageCircle, Share2, Instagram, Facebook, Linkedin, Twitter, Shield, Send, X, Phone, Mail, Sparkles, Zap, Award, Coffee, TrendingUp, MapPin, Clock, Wheat } from 'lucide-react';
import axios from 'axios';
import '../styles/Home.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// ============================================================================
// LABEL DATA - THE GOSPEL - All from I Love Camelina<sup>®</sup> Official Labels
// ============================================================================

// Nutrition Facts (per 1 tbsp / 15ml / 14g)
const NUTRITION_FACTS = {
  servingSize: "1 tbsp (15 ml / 14g)",
  servingsPerBottle: 33,
  calories: 120,
  totalFat: { amount: "14g", dv: "22%" },
  saturatedFat: { amount: "2g", dv: "10%" },
  polyunsaturatedFat: "8g",
  omega6: "3g",
  omega3: "5g (5,000mg)",
  monounsaturatedFat: "4g",
  cholesterol: { amount: "0mg", dv: "0%" },
  sodium: { amount: "0mg", dv: "0%" },
  totalCarbs: { amount: "0g", dv: "0%" },
  protein: "0g",
  vitaminE: { amount: "1.13mg", dv: "8%" },
  ingredient: "Camelina oil"
};

// Non-GMO Project Verified Logo URL
const NON_GMO_LOGO = "https://customer-assets.emergentagent.com/job_recipeai-platform/artifacts/gyh9ext4_image.png";

// Certifications & Allergen Icons
const CERTIFICATIONS = {
  nonGMO: { name: "Non-GMO Project Verified", logo: NON_GMO_LOGO },
  vegan: { name: "Vegan", icon: "🌱" },
  peanutFree: { name: "Peanut Free", icon: "🥜❌" },
  sesameFree: { name: "Sesame Free", icon: "🌾❌" },
  kosher: { name: "Kosher (MK Pareve)", icon: "✡️" },
  plantBased: { name: "Plant-Based Omega-3 Source", icon: "🌿" },
  madeInCanada: { name: "Made in Canada", icon: "🇨🇦" }
};

// Key Product Features from Label
const PRODUCT_FEATURES = {
  smokePoint: "475°F",
  omega3PerTbsp: "5,000mg",
  shelfLife: "30+ months",
  refrigeration: "No refrigeration required",
  storage: "Store at room temperature away from light",
  bottleType: "Dark glass bottle (UV protected)"
};

// Camelina Cares - 1% of sales donated
const CAMELINA_CARES = {
  percentage: "1%",
  causes: "Alzheimer's, dementia, and heart disease charities",
  mission: "1% of our sales support brain and heart health through donations to Alzheimer's, dementia, and heart disease charities, clinical research, and sustainable agriculture initiatives."
};

// Contact Info
const CONTACT_INFO = {
  phone: "(833) OMEGA-3-0",
  phoneFormatted: "833-664-3230",
  email: "Info@ILoveCamelina.com",
  website: "www.ILoveCamelina.com",
  social: "@ILoveCamelina"
};

// Better Oil Challenge
const BETTER_OIL_CHALLENGE = {
  headline: "THE BETTER OIL CHALLENGE",
  intro: "Most oils don't deserve a spot in your kitchen. Camelina is different.",
  description: "It's cold-pressed, clean, and packed with plant-based Omega-3s — with a smoke point that stands up to serious cooking.",
  challenge: "Use it for 30 days. Sauté, roast, bake, add to your smoothie, or drizzle on your favorite proteins. See how it fits your lifestyle. Feel how it fits your body.",
  closing: "Camelina isn't just better. It's the upgrade your oil aisle never offered."
};

// Farm Story
const FARM_STORY = {
  location: "Quebec, Canada",
  heritage: "6th Generation Family Farm",
  producer: "Oliméga Inc.",
  address: "519 rang des Sloan, St-Édouard, QC Canada J0L 1Y0",
  distributor: "I Love Camelina Inc., 1199 US Hwy 22, Mountainside, NJ 07092"
};

// ============================================================================
// END LABEL DATA
// ============================================================================

// Trending Recipes - Updated monthly from Google Trends / AllRecipes
const TRENDING_RECIPES = [
  "Prime Rib",
  "Beef Wellington", 
  "Honey Glazed Ham",
  "Creamy Tuscan Chicken",
  "Sugar Cookies",
  "Lasagna",
  "Beef Stew",
  "Air Fryer Salmon"
];

// Product URLs - REAL Shopify Links
const PRODUCT_URLS = {
  virgin: "https://www.ilovecamelina.com/products/i-love-camelina%E2%84%A2-virgin-camelina-oil-500-ml-cold-pressed-omega-3-rich-high-smoke-point",
  roasted: "https://www.ilovecamelina.com/products/i-love-camelina%E2%84%A2-roasted-reserve-camelina-oil-500-ml-premium-cold-pressed-high-heat-omega-3-rich-non-gmo-cooking-oil-copy",
  duo: "https://www.ilovecamelina.com/collections/all",
  buyNow: "https://www.ilovecamelina.com/collections/all"
};

// Product Images - REAL bottles from Shopify CDN
const PRODUCT_IMAGES = {
  virgin: "https://static.prod-images.emergentagent.com/jobs/8177d08e-334f-49d5-b1c4-e05d9088ac9f/images/efcf8be1305e986510e4afe6f8970fe41b653151c85aebc16cbb350bd5095b71.png",
  virginAlt: "https://static.prod-images.emergentagent.com/jobs/8177d08e-334f-49d5-b1c4-e05d9088ac9f/images/efcf8be1305e986510e4afe6f8970fe41b653151c85aebc16cbb350bd5095b71.png",
  virginAlt2: "https://static.prod-images.emergentagent.com/jobs/8177d08e-334f-49d5-b1c4-e05d9088ac9f/images/efcf8be1305e986510e4afe6f8970fe41b653151c85aebc16cbb350bd5095b71.png",
  roasted: "https://static.prod-images.emergentagent.com/jobs/8177d08e-334f-49d5-b1c4-e05d9088ac9f/images/5dff1e1265150399f9d71712e02e7473908c964651c23179d5a25ce0876c717a.png",
  roastedAlt: "https://static.prod-images.emergentagent.com/jobs/8177d08e-334f-49d5-b1c4-e05d9088ac9f/images/5dff1e1265150399f9d71712e02e7473908c964651c23179d5a25ce0876c717a.png",
  duo: "https://customer-assets.emergentagent.com/job_camelina-recipe-ai/artifacts/rupj40ih_IMG_0962.png"
};

// Real bottle photos from user - for use alongside recipes
const BOTTLE_IMAGES = {
  virgin: "https://static.prod-images.emergentagent.com/jobs/8177d08e-334f-49d5-b1c4-e05d9088ac9f/images/efcf8be1305e986510e4afe6f8970fe41b653151c85aebc16cbb350bd5095b71.png",
  roasted: "https://static.prod-images.emergentagent.com/jobs/8177d08e-334f-49d5-b1c4-e05d9088ac9f/images/5dff1e1265150399f9d71712e02e7473908c964651c23179d5a25ce0876c717a.png",
  duoMarble: "https://customer-assets.emergentagent.com/job_camelina-recipe-ai/artifacts/rupj40ih_IMG_0962.png",
  duoField: "https://customer-assets.emergentagent.com/job_camelina-recipe-ai/artifacts/v41l78k8_Photoroom_20251019_152101%20%281%29.jpeg",
  lifestyle: "https://customer-assets.emergentagent.com/job_camelina-recipe-ai/artifacts/j5yg8xe7_image5.jpeg",
  sunset: "https://customer-assets.emergentagent.com/job_camelina-recipe-ai/artifacts/n6gvkggn_image7.jpeg"
};

// Hero Food Collage Images (NO bottles - just cooking/food shots)
const HERO_FOOD_IMAGES = [
  "https://cdn.shopify.com/s/files/1/0637/3854/4216/files/I-Love-Camelina-Virgin-Cooking-Oil-Healhty.jpg?v=1768200999",
  "https://cdn.shopify.com/s/files/1/0637/3854/4216/files/Roasted_Camelina_Oil-Healthy_Cooking_Oil.jpg?v=1768201127"
];

// Background and Section Images
const CAMELINA_FIELD_FARM_IMAGE = "https://customer-assets.emergentagent.com/job_camelina-recipe-ai/artifacts/0fyl4pyt_healthy-cooking-oil-camelina.png";
const CAMELINA_CARES_BADGE = "https://customer-assets.emergentagent.com/job_camelina-recipe-ai/artifacts/uuihc7qb_image.png";
const GREEN_FLOWING_PATTERN = "https://static.prod-images.emergentagent.com/jobs/defc7ec1-870a-4522-b4a9-d05740602351/images/4c864178e3e25874ff4c9c96e8285afb3afdd74713d4cbf3e3bef049cbbdc0dc.png";

// Social Links (EXACT)
const SOCIAL_LINKS = {
  instagram: "https://www.instagram.com/ilovecamelina",
  tiktok: "https://www.tiktok.com/@ilovecamelina",
  twitter: "https://x.com/ilovecamelina",
  linkedin: "https://www.linkedin.com/company/ilovecamelina"
};

const LOGO_URL = "https://customer-assets.emergentagent.com/job_camelina-landing/artifacts/d9aarb65_I-LOVE-CAMELINA-CHOCOLATE-CHIP-COOkies-Omega-3.png";

// Hero and Background Images
const HERO_IMAGE = "https://static.prod-images.emergentagent.com/jobs/e705b398-5250-45d5-8410-98789249fe48/images/440a6a448801425797e7ca9268d50d8d0f58a253f5f84f96e5b7091961a03d6d.png";
const CAMELINA_FIELD_IMAGE = "https://static.prod-images.emergentagent.com/jobs/4dd04bd1-149e-4bec-85cf-e315d81ce3fe/images/7b93732159ad0105341b41a1c58fde36260993cd26c45eae1d1cc711df3a8f65.png";

// Oil Types for Survey (allow multiple selection)
const OIL_TYPES = [
  "Olive Oil",
  "Avocado Oil",
  "Vegetable Oil",
  "Canola Oil",
  "Coconut Oil",
  "Butter",
  "Ghee",
  "Beef Tallow",
  "Lard",
  "Sunflower Oil",
  "Peanut Oil",
  "Sesame Oil",
  "Grapeseed Oil",
  "Corn Oil",
  "Soybean Oil",
  "MCT Oil",
  "Flaxseed Oil",
  "Other"
];

// Oil Brands by Oil Type - Comprehensive list for market research
const OIL_BRANDS = {
  "Olive Oil": ["Graza", "California Olive Ranch", "Bertolli", "Colavita", "Filippo Berio", "Kirkland", "Pompeian", "Star", "Whole Foods 365", "Trader Joe's", "Brightland", "Partanna", "Lucini", "Napa Valley Naturals", "Olio Santo", "Other"],
  "Avocado Oil": ["Chosen Foods", "Primal Kitchen", "Spectrum", "Trader Joe's", "Whole Foods 365", "Kirkland", "Nutiva", "BetterBody Foods", "La Tourangelle", "Avohass", "Thrive Market", "Kevala", "Other"],
  "Vegetable Oil": ["Crisco", "Wesson", "Mazola", "Great Value", "Whole Foods 365", "Kirkland", "LouAna", "Other"],
  "Canola Oil": ["Wesson", "Crisco", "Spectrum", "LouAna", "Whole Foods 365", "Great Value", "Kirkland", "Other"],
  "Coconut Oil": ["Nutiva", "Carrington Farms", "Viva Naturals", "Nature's Way", "Whole Foods 365", "Trader Joe's", "Kirkland", "Dr. Bronner's", "Spectrum", "Garden of Life", "Other"],
  "Butter": ["Kerrygold", "Land O'Lakes", "Plugra", "Organic Valley", "Whole Foods 365", "Trader Joe's", "Challenge", "Tillamook", "Finlandia", "Vermont Creamery", "Other"],
  "Ghee": ["4th & Heart", "Ancient Organics", "Tin Star Foods", "Organic Valley", "Whole Foods 365", "Trader Joe's", "Carrington Farms", "Other"],
  "Beef Tallow": ["EPIC", "Fatworks", "US Wellness Meats", "South Chicago Packing", "Grass Roots", "Other"],
  "Lard": ["Fatworks", "US Wellness Meats", "Morrell", "Armour", "Proper Food", "Other"],
  "Sunflower Oil": ["Spectrum", "Thrive Market", "La Tourangelle", "Whole Foods 365", "Trader Joe's", "Daisy", "Other"],
  "Peanut Oil": ["LouAna", "Planters", "Spectrum", "Whole Foods 365", "Hollywood", "Other"],
  "Sesame Oil": ["Kadoya", "Lee Kum Kee", "La Tourangelle", "Whole Foods 365", "Trader Joe's", "Roland", "Other"],
  "Grapeseed Oil": ["Pompeian", "La Tourangelle", "Spectrum", "Whole Foods 365", "Trader Joe's", "Napa Valley Naturals", "Other"],
  "Corn Oil": ["Mazola", "Wesson", "Great Value", "Whole Foods 365", "Other"],
  "Soybean Oil": ["Wesson", "Crisco", "Great Value", "Other"],
  "MCT Oil": ["Bulletproof", "Sports Research", "Perfect Keto", "Onnit", "Garden of Life", "Viva Naturals", "Other"],
  "Flaxseed Oil": ["Barlean's", "Spectrum", "Nature Made", "Nature's Way", "Whole Foods 365", "Other"],
  "Other": ["Other"]
};

// Footer Links - Real Shopify URLs
const FOOTER_URLS = {
  aboutUs: "https://www.ilovecamelina.com/pages/about-i-love-camelina",
  nutrition: "https://www.ilovecamelina.com/pages/benefits-of-camelina-oil",
  research: "https://www.ilovecamelina.com/pages/what-is-camelina",
  privacy: "https://www.ilovecamelina.com/policies/privacy-policy",
  terms: "https://www.ilovecamelina.com/policies/terms-of-service",
  contact: "https://www.ilovecamelina.com/pages/contact"
};

const ROTATING_PLACEHOLDERS = [
  "cookies",
  "lasagna",
  "banana bread",
  "stir fry",
  "meatloaf",
  "caesar dressing",
  "chocolate cake",
  "roasted vegetables"
];

// Food images by cooking method - vibrant, distinct photography
const COOKING_IMAGES = {
  oven: "https://images.unsplash.com/photo-1518133299975-8e1b628e1cfd?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzZ8MHwxfHNlYXJjaHwxfHxyb2FzdGVkJTIwdmVnZXRhYmxlcyUyMHRyYXklMjBvdmVufGVufDB8fHx8MTc4Mjg0MzUzM3ww&ixlib=rb-4.1.0&q=85",
  stovetop: "https://images.unsplash.com/photo-1601226809816-b8c32440158a?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2MTJ8MHwxfHNlYXJjaHwxfHx2ZWdldGFibGUlMjBzdGlyJTIwZnJ5JTIwd29rJTIwY29va2luZ3xlbnwwfHx8fDE3ODMwMzc2Mzh8MA&ixlib=rb-4.1.0&q=85",
  air_fryer: "https://images.pexels.com/photos/35285814/pexels-photo-35285814.jpeg",
  grill: "https://images.pexels.com/photos/18824025/pexels-photo-18824025.jpeg",
  no_cook: "https://images.pexels.com/photos/4887994/pexels-photo-4887994.jpeg"
};

// Benefit icons mapping
const BENEFIT_ICONS = {
  "Neutral flavor": <Leaf size={20} />,
  "Stable up to 475": <Flame size={20} />,
  "omega-3": <Heart size={20} />,
  "1:1": <CheckCircle size={20} />,
  "saturated fat": <Zap size={20} />,
  "trans fats": <Shield size={20} />,
  "cold-pressed": <Award size={20} />,
  default: <CheckCircle size={20} />
};

const getBenefitIcon = (benefit) => {
  for (const [key, icon] of Object.entries(BENEFIT_ICONS)) {
    if (benefit.toLowerCase().includes(key.toLowerCase())) {
      return icon;
    }
  }
  return BENEFIT_ICONS.default;
};

const Home = () => {
  const [recipeText, setRecipeText] = useState('');
  const [placeholder, setPlaceholder] = useState(ROTATING_PLACEHOLDERS[0]);
  const [isOptimizing, setIsOptimizing] = useState(false);
  const [loadingFact, setLoadingFact] = useState(0);
  const [optimizedResult, setOptimizedResult] = useState(null);
  
  // Fun facts to show while loading
  const LOADING_FACTS = [
    { emoji: "🧠", text: "5,000mg omega-3s per tablespoon..." },
    { emoji: "🔥", text: "Stable up to 475°F - perfect for any cooking..." },
    { emoji: "💛", text: "From a 6th generation Quebec farm..." },
    { emoji: "✨", text: "Cold-pressed, non-GMO, and delicious..." },
    { emoji: "🥗", text: "Creating your personalized recipe..." }
  ];
  
  // Rotate loading facts
  useEffect(() => {
    let interval;
    if (isOptimizing) {
      interval = setInterval(() => {
        setLoadingFact(prev => (prev + 1) % LOADING_FACTS.length);
      }, 1500);
    }
    return () => clearInterval(interval);
  }, [isOptimizing]);
  
  // Lead Capture State
  const [showEmailCapture, setShowEmailCapture] = useState(false);
  const [showSmsCapture, setShowSmsCapture] = useState(false);
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [emailOptIn, setEmailOptIn] = useState(true);
  const [smsOptIn, setSmsOptIn] = useState(true);
  const [isSubmittingEmail, setIsSubmittingEmail] = useState(false);
  const [isSubmittingSms, setIsSubmittingSms] = useState(false);
  const [leadCaptured, setLeadCaptured] = useState(false);
  
  // Recipe Gating - user must take action to see full recipe
  const [recipeUnlocked, setRecipeUnlocked] = useState(false);
  
  // Share Modal
  const [showShareModal, setShowShareModal] = useState(false);
  
  // Ask ChatGPT State
  const [chatQuestion, setChatQuestion] = useState('');
  const [chatAnswer, setChatAnswer] = useState('');
  const [isAskingChatGPT, setIsAskingChatGPT] = useState(false);

  // Oil Poll State - allow multiple oil selection
  const [pollStep, setPollStep] = useState(0); // 0=not started, 1=oil selection, 2=brand selection, 3=contact form, 4=complete
  const [pollData, setPollData] = useState({
    firstName: '',
    email: '',
    phone: '',
    selectedOils: [], // Multiple oils
    selectedBrands: {} // Brands per oil type
  });
  const [pollResult, setPollResult] = useState(null);
  const [isSubmittingPoll, setIsSubmittingPoll] = useState(false);
  const [loadingSharedRecipe, setLoadingSharedRecipe] = useState(false);

  // Load shared recipe from URL parameter
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const recipeId = urlParams.get('recipe');
    
    if (recipeId) {
      loadSharedRecipe(recipeId);
    }
  }, []);

  const loadSharedRecipe = async (recipeId) => {
    setLoadingSharedRecipe(true);
    try {
      const response = await axios.get(`${API}/recipe-data/${recipeId}`);
      if (response.data.success && response.data.recipe_data) {
        setOptimizedResult(response.data.recipe_data);
        setRecipeUnlocked(true);
        setLeadCaptured(true);
        // Scroll to recipe after loading
        setTimeout(() => {
          scrollToSection('recipe-output');
        }, 500);
      }
    } catch (error) {
      console.error('Error loading shared recipe:', error);
    } finally {
      setLoadingSharedRecipe(false);
    }
  };

  // Rotate placeholders
  useEffect(() => {
    const interval = setInterval(() => {
      setPlaceholder(prev => {
        const currentIndex = ROTATING_PLACEHOLDERS.indexOf(prev);
        const nextIndex = (currentIndex + 1) % ROTATING_PLACEHOLDERS.length;
        return ROTATING_PLACEHOLDERS[nextIndex];
      });
    }, 3000);
    return () => clearInterval(interval);
  }, []);

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const handleOptimizeRecipe = async (overrideRecipe = null) => {
    // Handle case where event object is passed instead of string
    const searchText = typeof overrideRecipe === 'string' ? overrideRecipe : recipeText;
    if (!searchText || !searchText.trim()) {
      alert('Please enter a recipe or dish name');
      return;
    }

    // Update input if using override
    if (typeof overrideRecipe === 'string' && overrideRecipe) {
      setRecipeText(overrideRecipe);
    }

    setIsOptimizing(true);
    setOptimizedResult(null);
    
    try {
      const response = await axios.post(`${API}/optimize-recipe`, {
        recipe_text: searchText
      });
      setOptimizedResult(response.data);
      
      // Show email capture modal after result
      setTimeout(() => {
        setShowEmailCapture(true);
      }, 2000);
      
      setTimeout(() => {
        scrollToSection('recipe-output');
      }, 300);
    } catch (error) {
      console.error('Recipe optimization error:', error);
      alert('Something went wrong. Please try again.');
    } finally {
      setIsOptimizing(false);
    }
  };

  const handleTryAnother = () => {
    setRecipeText('');
    setOptimizedResult(null);
    setLeadCaptured(false);
    setRecipeUnlocked(false);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleShare = (platform) => {
    const shareText = `I just upgraded my ${optimizedResult?.dish_name || 'recipe'} with I Love Camelina<sup>®</sup> — ${optimizedResult?.total_omega3_mg?.toLocaleString() || '5,000'}+ mg omega-3s!`;
    const shareUrl = window.location.href;
    
    const urls = {
      twitter: `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(shareUrl)}`,
      facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}&quote=${encodeURIComponent(shareText)}`,
      linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`,
      instagram: SOCIAL_LINKS.instagram
    };

    if (urls[platform]) {
      window.open(urls[platform], '_blank', 'width=600,height=400');
    }
    setShowShareModal(false);
  };

  const handleEmailSubmit = async (e) => {
    e.preventDefault();
    if (!email) return;
    
    setIsSubmittingEmail(true);
    try {
      await axios.post(`${API}/capture-email`, {
        email,
        recipe_data: optimizedResult,
        original_recipe: recipeText,
        opt_in: emailOptIn
      });
      setShowEmailCapture(false);
      setShowSmsCapture(true);
      setLeadCaptured(true);
      setRecipeUnlocked(true); // Unlock full recipe after email
    } catch (error) {
      console.error('Email capture error:', error);
      alert('Something went wrong. Please try again.');
    } finally {
      setIsSubmittingEmail(false);
    }
  };

  // Handle social follow - unlocks recipe
  const handleSocialFollow = (platform) => {
    window.open(SOCIAL_LINKS[platform], '_blank');
    setRecipeUnlocked(true);
  };

  const handleSmsSubmit = async (e) => {
    e.preventDefault();
    
    setIsSubmittingSms(true);
    try {
      if (phone) {
        await axios.post(`${API}/capture-sms`, {
          email,
          phone,
          opt_in: smsOptIn
        });
      }
      setShowSmsCapture(false);
    } catch (error) {
      console.error('SMS capture error:', error);
    } finally {
      setIsSubmittingSms(false);
    }
  };

  const handleAskChatGPT = async (question) => {
    const q = question || chatQuestion;
    if (!q.trim()) return;
    
    setIsAskingChatGPT(true);
    setChatAnswer('');
    
    try {
      const response = await axios.post(`${API}/ask-chatgpt`, {
        question: q
      });
      setChatAnswer(response.data.answer);
    } catch (error) {
      console.error('Ask ChatGPT error:', error);
      setChatAnswer('Sorry, I couldn\'t process that question. Please try again.');
    } finally {
      setIsAskingChatGPT(false);
    }
  };

  const presetQuestions = [
    "Is camelina oil healthier than olive oil?",
    "What cooking oil has the most omega-3s?",
    "Is olive oil safe for high-heat cooking?",
    "What's the healthiest oil for frying?"
  ];

  // Oil Poll Handlers - Multiple selection
  const handleOilToggle = (oil) => {
    setPollData(prev => {
      const currentOils = prev.selectedOils;
      if (currentOils.includes(oil)) {
        return { ...prev, selectedOils: currentOils.filter(o => o !== oil) };
      } else {
        return { ...prev, selectedOils: [...currentOils, oil] };
      }
    });
  };

  const handleBrandToggle = (oil, brand) => {
    setPollData(prev => {
      const currentBrands = prev.selectedBrands[oil] || [];
      if (currentBrands.includes(brand)) {
        return { 
          ...prev, 
          selectedBrands: { 
            ...prev.selectedBrands, 
            [oil]: currentBrands.filter(b => b !== brand) 
          } 
        };
      } else {
        return { 
          ...prev, 
          selectedBrands: { 
            ...prev.selectedBrands, 
            [oil]: [...currentBrands, brand] 
          } 
        };
      }
    });
  };

  const handleOilsNext = () => {
    if (pollData.selectedOils.length === 0) {
      alert('Please select at least one oil');
      return;
    }
    setPollStep(2); // Move to brand selection
  };

  const handleBrandsNext = () => {
    setPollStep(3); // Move to contact form
  };

  const handlePollSubmit = async (e) => {
    e.preventDefault();
    if (!pollData.firstName || !pollData.email || pollData.selectedOils.length === 0) return;
    
    setIsSubmittingPoll(true);
    try {
      const response = await axios.post(`${API}/oil-poll`, {
        first_name: pollData.firstName,
        email: pollData.email,
        phone: pollData.phone || null,
        current_oil: pollData.selectedOils.join(', '),
        current_brand: Object.entries(pollData.selectedBrands)
          .map(([oil, brands]) => `${oil}: ${brands.join(', ')}`)
          .join(' | ') || null
      });
      setPollResult(response.data);
      setPollStep(4); // Show results
    } catch (error) {
      console.error('Poll submission error:', error);
      alert('Something went wrong. Please try again.');
    } finally {
      setIsSubmittingPoll(false);
    }
  };

  // Social sharing for poll results (without coupon for viral spread)
  const handleShareResults = (platform) => {
    const shareUrl = 'https://www.ilovecamelina.com';
    const shareText = `I just discovered the healthiest cooking oil! I Love Camelina<sup>®</sup> has 5,000mg of omega-3s per tablespoon and a 475°F smoke point. Better for heart, brain, and inflammation support.`;
    const hashtags = 'ILoveCamelina,HealthyCooking,Omega3,CookSmarter';
    
    const urls = {
      facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}&quote=${encodeURIComponent(shareText)}`,
      twitter: `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(shareUrl)}&hashtags=${hashtags}`,
      linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`,
      email: `mailto:?subject=${encodeURIComponent('Discover the Healthiest Cooking Oil - I Love Camelina<sup>®</sup>')}&body=${encodeURIComponent(shareText + '\n\nLearn more: ' + shareUrl)}`,
      copy: shareUrl
    };
    
    if (platform === 'copy') {
      navigator.clipboard.writeText(shareUrl + '\n\n' + shareText);
      alert('Link copied to clipboard!');
    } else if (platform === 'email') {
      window.location.href = urls.email;
    } else {
      window.open(urls[platform], '_blank', 'width=600,height=400');
    }
  };

  // Social sharing for recipe results - saves recipe for dynamic SEO
  const handleShareRecipe = async (platform) => {
    if (!optimizedResult) return;
    
    try {
      // Save recipe to backend for proper social media crawling
      const response = await axios.post(`${API}/save-shareable-recipe`, {
        dish_name: optimizedResult.dish_name,
        recommended_oil: optimizedResult.recommended_oil,
        total_omega3_mg: optimizedResult.total_omega3_mg,
        why_it_works: optimizedResult.why_it_works,
        recipe_image_url: optimizedResult.recipe_image_url || getCookingImage(),
        original_fat: optimizedResult.original_fat,
        cooking_method: optimizedResult.cooking_method,
        optimized_recipe: optimizedResult.optimized_recipe
      });
      
      const shareUrl = response.data.share_url;
      const shareText = `🥗 Check out this healthier ${optimizedResult.dish_name} recipe! Made with I Love Camelina<sup>®</sup> omega-3 cooking oil - ${optimizedResult.total_omega3_mg?.toLocaleString()}mg of plant-based omega-3s! #HealthyRecipe #Omega3 #ILoveCamelina`;
      
      const urls = response.data.social_share_urls || {
        facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`,
        twitter: `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(shareUrl)}`,
        linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`,
        pinterest: `https://pinterest.com/pin/create/button/?url=${encodeURIComponent(shareUrl)}&description=${encodeURIComponent(shareText)}&media=${encodeURIComponent(optimizedResult.recipe_image_url || getCookingImage())}`,
        email: `mailto:?subject=${encodeURIComponent(`Healthy ${optimizedResult.dish_name} Recipe`)}&body=${encodeURIComponent(shareText + '\n\nView recipe: ' + shareUrl)}`,
        copy: shareUrl
      };
      
      if (platform === 'copy') {
        navigator.clipboard.writeText(shareUrl + '\n\n' + shareText);
        alert('Recipe link copied to clipboard! When shared, it will show the recipe image and details.');
      } else if (platform === 'email') {
        window.location.href = urls.email;
      } else {
        window.open(urls[platform], '_blank', 'width=600,height=400');
      }
    } catch (error) {
      console.error('Error creating shareable recipe:', error);
      // Fallback to basic sharing without dynamic meta tags
      const shareUrl = `https://www.ilovecamelina.com`;
      const shareText = `Check out this healthier ${optimizedResult.dish_name} recipe with I Love Camelina<sup>®</sup>!`;
      
      if (platform === 'copy') {
        navigator.clipboard.writeText(shareUrl + '\n\n' + shareText);
        alert('Link copied!');
      } else if (platform === 'email') {
        window.location.href = `mailto:?subject=${encodeURIComponent(`Healthy ${optimizedResult.dish_name} Recipe`)}&body=${encodeURIComponent(shareText)}`;
      } else {
        window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`, '_blank');
      }
    }
  };

  // Get cooking image based on method
  const getCookingImage = () => {
    // First, use the dynamic recipe-specific image if available
    if (optimizedResult?.recipe_image_url) {
      return optimizedResult.recipe_image_url;
    }
    // Fallback to method-based image
    const method = optimizedResult?.cooking_method || 'oven';
    return COOKING_IMAGES[method] || COOKING_IMAGES.oven;
  };

  // Get the recommended bottle image
  const getRecommendedBottleImage = () => {
    return optimizedResult?.recommended_oil === 'Roasted' 
      ? BOTTLE_IMAGES.roasted 
      : BOTTLE_IMAGES.virgin;
  };

  const getRecommendedProductUrl = () => {
    return optimizedResult?.recommended_oil === 'Roasted'
      ? PRODUCT_URLS.roasted
      : PRODUCT_URLS.virgin;
  };

  return (
    <div className="camelina-page">
      {/* Dynamic SEO Meta Tags */}
      <Helmet>
        {optimizedResult ? (
          <>
            {/* Dynamic Recipe SEO */}
            <title>{optimizedResult.dish_name} | Healthy Recipe with {optimizedResult.total_omega3_mg?.toLocaleString()}mg Omega-3s | I Love Camelina®</title>
            <meta name="description" content={`Try this healthier ${optimizedResult.dish_name}! Upgraded with I Love Camelina<sup>®</sup> ${optimizedResult.recommended_oil} oil for ${optimizedResult.total_omega3_mg?.toLocaleString()}mg of omega-3s. ${optimizedResult.why_it_works?.slice(0, 120)}...`} />
            <meta name="keywords" content={`${optimizedResult.dish_name?.toLowerCase()}, healthy ${optimizedResult.dish_name?.toLowerCase()}, omega-3 recipe, camelina oil recipe, heart healthy recipe, ${optimizedResult.recommended_oil?.toLowerCase()} camelina oil, healthy cooking, anti-inflammatory recipe`} />
            
            {/* Open Graph for Social Sharing */}
            <meta property="og:type" content="article" />
            <meta property="og:title" content={`${optimizedResult.dish_name} | Healthy Recipe with ${optimizedResult.total_omega3_mg?.toLocaleString()}mg Omega-3s`} />
            <meta property="og:description" content={`Healthier ${optimizedResult.dish_name} made with I Love Camelina<sup>®</sup> omega-3 oil. ${optimizedResult.total_omega3_mg?.toLocaleString()}mg omega-3s per serving!`} />
            <meta property="og:image" content={optimizedResult.recipe_image_url || 'https://www.ilovecamelina.com/cdn/shop/files/hero-food-collage.png'} />
            <meta property="og:url" content={`https://www.ilovecamelina.com/recipes/${optimizedResult.dish_name?.toLowerCase().replace(/\s+/g, '-')}`} />
            
            {/* Twitter Card */}
            <meta name="twitter:card" content="summary_large_image" />
            <meta name="twitter:title" content={`${optimizedResult.dish_name} | Omega-3 Rich Recipe`} />
            <meta name="twitter:description" content={`${optimizedResult.total_omega3_mg?.toLocaleString()}mg omega-3s! Made healthier with I Love Camelina<sup>®</sup>`} />
            <meta name="twitter:image" content={optimizedResult.recipe_image_url || 'https://www.ilovecamelina.com/cdn/shop/files/hero-food-collage.png'} />
          </>
        ) : (
          <>
            {/* Default Landing Page SEO */}
            <title>I Love Camelina® | #1 Omega-3 Cooking Oil | AI Recipe Optimizer | Healthy Oil Alternative</title>
            <meta name="description" content="I Love Camelina<sup>®</sup> is the healthiest cooking oil with 5,000mg omega-3s per tablespoon. FREE AI Recipe Optimizer upgrades ANY recipe — replace butter, olive oil, or vegetable oil instantly. Heart-healthy, brain-boosting, anti-inflammatory. Get 10% off!" />
          </>
        )}
      </Helmet>

      {/* Header */}
      <header className="camelina-header">
        <div className="container">
          <div className="header-content">
            <div className="logo-wrapper">
              <img 
                src={LOGO_URL} 
                alt="I Love Camelina<sup>®</sup>" 
                className="logo" 
                data-testid="header-logo"
              />
            </div>
            <nav className="nav-actions">
              <button 
                className="btn-learn btn-sm"
                onClick={() => scrollToSection('why-different')}
                data-testid="nav-why-different"
              >
                Why Different?
              </button>
              <button 
                className="btn-buy btn-sm"
                onClick={() => scrollToSection('products')}
                data-testid="nav-shop-now"
              >
                Shop Now
              </button>
            </nav>
          </div>
        </div>
      </header>

      {/* Hero - 50% Viewport with Food Collage (NO bottles - just food) */}
      <section className="hero-section">
        {/* PROMINENT LOGO - Top Right of Hero */}
        <div className="hero-logo-prominent">
          <a href="https://www.ilovecamelina.com" target="_blank" rel="noopener noreferrer">
            <img src={LOGO_URL} alt="I Love Camelina<sup>®</sup>" className="hero-logo-image" />
          </a>
        </div>
        
        <div className="container">
          <div className="hero-content">
            <h1 className="heading-hero" data-testid="hero-title">
              Upgrade Any Recipe with I Love Camelina<sup>®</sup>
            </h1>
            
            {/* Google-Style Search */}
            <div className="hero-tool" data-testid="recipe-optimizer-input">
              <input
                type="text"
                className="recipe-input-hero" 
                placeholder={`Try "${placeholder}"...`}
                value={recipeText}
                onChange={(e) => setRecipeText(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleOptimizeRecipe()}
                data-testid="recipe-input"
              />
              <div className="hero-tool-actions">
                <button 
                  className="btn-buy btn-large"
                  onClick={handleOptimizeRecipe}
                  disabled={isOptimizing}
                  data-testid="optimize-button"
                >
                  {isOptimizing ? (
                    <>
                      <Loader2 size={18} className="spinning" />
                      <span>Creating Magic...</span>
                    </>
                  ) : (
                    <>
                      <Sparkles size={18} />
                      <span>Optimize Recipe</span>
                    </>
                  )}
                </button>
                
                {/* Inline Loading Message - Not intrusive */}
                {isOptimizing && (
                  <div className="loading-inline" data-testid="loading-inline">
                    <div className="loading-spinner-small"></div>
                    <span className="loading-fact-inline">
                      {LOADING_FACTS[loadingFact].emoji} {LOADING_FACTS[loadingFact].text}
                    </span>
                  </div>
                )}
              </div>
            </div>

            {/* Trending Recipes Section - More Prominent */}
            <div className="trending-recipes-section" data-testid="trending-recipes">
              <div className="trending-header">
                <TrendingUp size={18} className="trending-icon-animated" />
                <span className="trending-title">🔥 Trending Now</span>
              </div>
              <div className="trending-pills">
                {TRENDING_RECIPES.map((recipe, idx) => (
                  <button
                    key={idx}
                    className="trending-pill-prominent"
                    onClick={() => {
                      setRecipeText(recipe);
                      handleOptimizeRecipe(recipe);
                    }}
                    data-testid={`trending-${idx}`}
                  >
                    <ChefHat size={14} />
                    {recipe}
                  </button>
                ))}
              </div>
              <p className="trending-note">Click any recipe to optimize with I Love Camelina<sup>®</sup></p>
            </div>

            {/* Secondary CTAs below search */}
            <div className="hero-secondary-ctas">
              <button 
                className="btn-learn"
                onClick={() => scrollToSection('why-different')}
                data-testid="hero-cta-why"
              >
                Why Camelina Is Different
              </button>
              <button 
                className="btn-learn"
                onClick={() => scrollToSection('products')}
                data-testid="hero-cta-shop"
              >
                Shop I Love Camelina<sup>®</sup>
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Live Recipe Output - THREE COLUMN LAYOUT */}
      {optimizedResult && (
        <section id="recipe-output" className="recipe-output-section" data-testid="recipe-output-section">
          <div className="container">
            {/* Header with dish name and omega badge */}
            <div className="output-header">
              <h2 className="heading-1">{optimizedResult.dish_name || 'Your Optimized Recipe'}</h2>
              <div className="omega3-badge" data-testid="omega3-badge">
                <Heart size={20} />
                <span>{optimizedResult.total_omega3_mg?.toLocaleString()} mg plant-based omega-3s</span>
              </div>
            </div>
            
            {/* PROMINENT OMEGA-3 NUTRITION BOX */}
            <div className="omega3-highlight-box" data-testid="omega3-highlight">
              <div className="omega3-highlight-header">
                <Heart size={28} className="omega3-heart" />
                <span>Plant-Based ALA Omega-3s from I Love Camelina®</span>
              </div>
              <div className="omega3-highlight-stats">
                <div className="omega3-stat total">
                  <span className="omega3-stat-number">{optimizedResult.total_omega3_mg?.toLocaleString() || '30,000'}</span>
                  <span className="omega3-stat-unit">mg total</span>
                  <span className="omega3-stat-label">in this recipe</span>
                </div>
                <div className="omega3-divider">÷</div>
                <div className="omega3-stat servings">
                  <span className="omega3-stat-number">{optimizedResult.servings || 4}</span>
                  <span className="omega3-stat-unit">servings</span>
                  <span className="omega3-stat-label">per recipe</span>
                </div>
                <div className="omega3-divider">=</div>
                <div className="omega3-stat per-serving">
                  <span className="omega3-stat-number">{optimizedResult.omega3_per_serving?.toLocaleString() || Math.round((optimizedResult.total_omega3_mg || 30000) / (optimizedResult.servings || 4)).toLocaleString()}</span>
                  <span className="omega3-stat-unit">mg per serving</span>
                  <span className="omega3-stat-label">of healthy omega-3s</span>
                </div>
              </div>
              <div className="omega3-highlight-tip">
                💡 <strong>Tip:</strong> Making for {(optimizedResult.servings || 4) * 2} people? Double the recipe for {((optimizedResult.total_omega3_mg || 30000) * 2).toLocaleString()}mg total omega-3s!
              </div>
            </div>
            
            {/* BIG HERO IMAGE - Recipe + Product Side by Side - BALANCED */}
            <div className="recipe-hero-showcase">
              <div className="recipe-hero-food">
                <img 
                  src={getCookingImage()} 
                  alt={optimizedResult.dish_name}
                  className="recipe-hero-image"
                  data-testid="recipe-hero-image"
                />
                <div className="recipe-image-badge">www.ILoveCamelina.com</div>
              </div>
              <div className={`recipe-hero-bottle ${optimizedResult.recommended_oil === 'Roasted' ? 'roasted-oil' : 'virgin-oil'}`}>
                <a href={getRecommendedProductUrl()} target="_blank" rel="noopener noreferrer">
                  <img 
                    src={getRecommendedBottleImage()} 
                    alt={`${optimizedResult.recommended_oil} Camelina Oil`}
                    className="recipe-bottle-image"
                  />
                </a>
                <div className="bottle-label">
                  <span className="bottle-type">{optimizedResult.recommended_oil} Camelina Oil</span>
                  <span className="bottle-brand">I Love Camelina<sup>®</sup></span>
                  <span className="bottle-website">www.ILoveCamelina.com</span>
                </div>
              </div>
            </div>

            {/* Recipe Info Grid */}
            <div className="recipe-info-grid">
              {/* Recipe Upgrade Card */}
              <div className="info-card upgrade-card">
                <h3 className="card-title">Recipe Upgrade</h3>
                <div className="swap-details">
                  <div className="swap-original">
                    <span className="swap-label">Original fat:</span>
                    <span className="swap-value">{optimizedResult.original_fat} ({optimizedResult.original_amount})</span>
                  </div>
                  <div className="swap-arrow">↓</div>
                  <div className="swap-upgraded">
                    <span className="swap-label">Upgraded to:</span>
                    <span className="swap-value">I Love Camelina<sup>®</sup> ({optimizedResult.camelina_amount})</span>
                  </div>
                </div>
                <div className="upgrade-icons">
                  <div className="upgrade-icon-item">
                    <Flame size={14} />
                    <span>475°F stable</span>
                  </div>
                  <div className="upgrade-icon-item">
                    <Heart size={14} />
                    <span>Omega-3 boost</span>
                  </div>
                  <div className="upgrade-icon-item">
                    <CheckCircle size={14} />
                    <span>1:1 swap</span>
                  </div>
                </div>
              </div>

              {/* Why It Works Card */}
              <div className="info-card benefits-card">
                <h3 className="card-title">Why {optimizedResult.recommended_oil} Works</h3>
                <p className="why-text">{optimizedResult.why_this_oil}</p>
                <div className="benefits-list">
                  {(optimizedResult.benefits || [
                    "Neutral flavor — won't overpower",
                    "Stable up to 475°F",
                    "~5,000 mg omega-3s per tablespoon",
                    "1:1 butter or oil replacement"
                  ]).slice(0, 4).map((benefit, idx) => (
                    <div key={idx} className="benefit-item">
                      {getBenefitIcon(benefit)}
                      <span>{benefit}</span>
                    </div>
                  ))}
                </div>
                {optimizedResult.heat_education && (
                  <div className="heat-warning">
                    <Flame size={18} />
                    <span>{optimizedResult.heat_education}</span>
                  </div>
                )}
              </div>

              {/* Buy Card - With Product Image */}
              <div className={`info-card buy-card ${optimizedResult.recommended_oil === 'Roasted' ? 'buy-card-roasted' : 'buy-card-virgin'}`}>
                <h3 className="card-title">Get {optimizedResult.recommended_oil} Camelina Oil</h3>
                <div className="buy-card-product">
                  <img 
                    src={getRecommendedBottleImage()} 
                    alt={`${optimizedResult.recommended_oil} Camelina Oil`}
                    className="buy-card-bottle"
                  />
                </div>
                <a href={getRecommendedProductUrl()} target="_blank" rel="noopener noreferrer" className="btn-shop-blue btn-large" data-testid="buy-recommended">
                  <ShoppingCart size={18} />
                  <span>Buy Now</span>
                </a>
                <p className="buy-card-website">www.ILoveCamelina.com</p>
                <p className="alternative-note">{optimizedResult.alternative_oil}</p>
              </div>
            </div>

            {/* Complete Recipe Section - GATED */}
            <div className={`complete-recipe-section ${!recipeUnlocked ? 'recipe-gated-section' : ''}`}>
              <div className="recipe-card-header">
                <div className="recipe-card-image-container">
                  <img 
                    src={getCookingImage()} 
                    alt={optimizedResult.dish_name}
                    className="recipe-card-image"
                  />
                  <div className="recipe-card-badge">
                    <span>www.ILoveCamelina.com</span>
                  </div>
                </div>
                <div className="recipe-card-header-content">
                  <h3 className="heading-2">Complete Recipe</h3>
                  <p className="recipe-subtitle">Made with I Love Camelina<sup>®</sup></p>
                  <p className="recipe-dish-name">{optimizedResult.dish_name}</p>
                </div>
              </div>
              
              {recipeUnlocked ? (
                <div className="recipe-text">{optimizedResult.optimized_recipe}</div>
              ) : (
                <>
                  <div className="recipe-text recipe-preview-blur">
                    {optimizedResult.optimized_recipe?.substring(0, 300)}...
                  </div>
                  <div className="recipe-gate-overlay">
                    <h3 className="gate-title">Want the full recipe?</h3>
                    <p className="gate-subtitle">Choose one option to unlock:</p>
                    <div className="gate-options">
                      <button 
                        className="btn-buy btn-large"
                        onClick={() => setShowEmailCapture(true)}
                        data-testid="gate-email-btn"
                      >
                        <Mail size={18} />
                        <span>Get it via Email</span>
                      </button>
                      <div className="gate-divider">or</div>
                      <div className="social-follow-options">
                        <button 
                          className="social-follow-btn"
                          onClick={() => handleSocialFollow('instagram')}
                          data-testid="gate-instagram-btn"
                        >
                          <Instagram size={18} />
                          <span>Follow</span>
                        </button>
                        <button 
                          className="social-follow-btn"
                          onClick={() => handleSocialFollow('tiktok')}
                          data-testid="gate-tiktok-btn"
                        >
                          <Coffee size={18} />
                          <span>Follow</span>
                        </button>
                        <button 
                          className="social-follow-btn"
                          onClick={() => handleSocialFollow('twitter')}
                          data-testid="gate-twitter-btn"
                        >
                          <Twitter size={18} />
                          <span>Follow</span>
                        </button>
                      </div>
                    </div>
                  </div>
                </>
              )}
            </div>

            {optimizedResult.usage_tip && (
              <div className="usage-tip">
                <CheckCircle size={20} />
                <p>{optimizedResult.usage_tip}</p>
              </div>
            )}
            
            {/* NUTRITION FACTS & CERTIFICATIONS for Recipe */}
            <div className="recipe-nutrition-certifications">
              {/* Certifications Row */}
              <div className="certifications-bar">
                <p className="certifications-label">CERTIFIED & TRUSTED</p>
                <div className="certifications-icons">
                  <div className="cert-item">
                    <img src={NON_GMO_LOGO} alt="Non-GMO" className="cert-nongmo-img" />
                  </div>
                  <div className="cert-item">
                    <span className="cert-icon">🌱</span>
                    <span className="cert-name">Vegan</span>
                  </div>
                  <div className="cert-item">
                    <span className="cert-icon">🥜</span>
                    <span className="cert-name">Peanut-Free</span>
                  </div>
                  <div className="cert-item">
                    <span className="cert-icon">🇨🇦</span>
                    <span className="cert-name">Made in Canada</span>
                  </div>
                  <div className="cert-item">
                    <span className="cert-icon">❄️</span>
                    <span className="cert-name">No Refrigeration</span>
                  </div>
                  <div className="cert-item">
                    <span className="cert-icon">📅</span>
                    <span className="cert-name">30+ Month Shelf Life</span>
                  </div>
                </div>
              </div>
              
              {/* Nutrition Facts Mini Panel */}
              <div className="nutrition-facts-panel compact">
                <h4 className="nutrition-title">I Love Camelina<sup>®</sup> Nutrition Facts</h4>
                <p className="nutrition-serving">Per 1 tbsp (15ml / 14g)</p>
                <div className="nutrition-grid">
                  <div className="nutrition-row">
                    <span className="nutrition-label">Calories</span>
                    <span className="nutrition-value">120</span>
                  </div>
                  <div className="nutrition-row">
                    <span className="nutrition-label">Total Fat</span>
                    <span className="nutrition-value">14g (22% DV)</span>
                  </div>
                  <div className="nutrition-row highlight-row">
                    <span className="nutrition-label">→ Omega-3 (ALA)</span>
                    <span className="nutrition-value omega-highlight">5,000mg</span>
                  </div>
                  <div className="nutrition-row">
                    <span className="nutrition-label">→ Omega-6</span>
                    <span className="nutrition-value">3g</span>
                  </div>
                  <div className="nutrition-row">
                    <span className="nutrition-label">Vitamin E</span>
                    <span className="nutrition-value">1.13mg (8% DV)</span>
                  </div>
                </div>
                <p className="nutrition-ingredient">Ingredients: 100% Cold-Pressed Camelina Oil</p>
              </div>
            </div>
            
            {/* 🏆 THE BETTER OIL CHALLENGE - Recipe Results */}
            <div className="better-oil-challenge">
              <div className="challenge-header">
                <span className="challenge-badge">🏆 THE BETTER OIL CHALLENGE 🏆</span>
                <h3 className="challenge-title">Use Camelina for 30 Days</h3>
              </div>
              <p className="challenge-description">
                Sauté, roast, bake, add to smoothies, or drizzle on your favorite proteins. 
                See how it fits your lifestyle. <strong>Feel how it fits your body.</strong>
              </p>
              <p className="challenge-quote">
                "Camelina isn't just better. It's the upgrade your oil aisle never offered."
              </p>
            </div>
            
            {/* 💚 CAMELINA CARES */}
            <div className="camelina-cares-box">
              <p className="cares-text">
                💚 <strong>Camelina Cares:</strong> 1% of every sale supports brain & heart health research — 
                Alzheimer's, dementia, and heart disease charities.
              </p>
            </div>

            {/* What's Next Section with Share */}
            <div className="whats-next-section" data-testid="viral-actions">
              <h2 className="heading-2" style={{marginBottom: '1.5rem', color: 'var(--camelina-green)'}}>What's Next?</h2>
              <div className="action-buttons">
                <a 
                  href={PRODUCT_URLS.buyNow}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-shop-blue btn-large"
                  data-testid="output-shop-button"
                >
                  <ShoppingCart size={18} />
                  <span>Shop at ilovecamelina.com</span>
                </a>
                <button 
                  className="btn-learn btn-large"
                  onClick={handleTryAnother}
                  data-testid="try-another-button"
                >
                  <ChefHat size={18} />
                  <span>Try Another Recipe</span>
                </button>
              </div>
              
              {/* Recipe Sharing Section - WITH Social Follow */}
              <div className="recipe-share-section">
                <h3 className="heading-3" style={{marginBottom: '0.5rem'}}>Share This Recipe</h3>
                <p className="share-subtitle">Help others discover healthier cooking with I Love Camelina<sup>®</sup></p>
                <div className="share-buttons recipe-share-buttons">
                  <button 
                    className="share-btn share-facebook"
                    onClick={() => handleShareRecipe('facebook')}
                    data-testid="recipe-share-facebook"
                  >
                    <Facebook size={18} />
                    <span>Share</span>
                  </button>
                  <button 
                    className="share-btn share-twitter"
                    onClick={() => handleShareRecipe('twitter')}
                    data-testid="recipe-share-twitter"
                  >
                    <Twitter size={18} />
                    <span>Tweet</span>
                  </button>
                  <button 
                    className="share-btn share-pinterest"
                    onClick={() => handleShareRecipe('pinterest')}
                    data-testid="recipe-share-pinterest"
                  >
                    <span style={{fontWeight: 'bold'}}>P</span>
                    <span>Pin</span>
                  </button>
                  <button 
                    className="share-btn share-linkedin"
                    onClick={() => handleShareRecipe('linkedin')}
                    data-testid="recipe-share-linkedin"
                  >
                    <Linkedin size={18} />
                    <span>Post</span>
                  </button>
                  <button 
                    className="share-btn share-email"
                    onClick={() => handleShareRecipe('email')}
                    data-testid="recipe-share-email"
                  >
                    <Mail size={18} />
                    <span>Email</span>
                  </button>
                  <button 
                    className="share-btn share-copy"
                    onClick={() => handleShareRecipe('copy')}
                    data-testid="recipe-share-copy"
                  >
                    <Share2 size={18} />
                    <span>Copy</span>
                  </button>
                </div>
                
                {/* Social Follow - Brand Colors */}
                <div className="social-follow-inline">
                  <p className="follow-label" style={{fontSize: '1rem', fontWeight: '600'}}>Follow for daily recipe upgrades:</p>
                  <div className="social-follow-buttons">
                    <a href={SOCIAL_LINKS.instagram} target="_blank" rel="noopener noreferrer" className="social-link-brand social-instagram" data-testid="follow-instagram">
                      <Instagram size={20} />
                      <span>Instagram</span>
                    </a>
                    <a href={SOCIAL_LINKS.tiktok} target="_blank" rel="noopener noreferrer" className="social-link-brand social-tiktok" data-testid="follow-tiktok">
                      <span style={{fontWeight: 'bold', fontSize: '1.1rem'}}>♪</span>
                      <span>TikTok</span>
                    </a>
                    <a href={SOCIAL_LINKS.twitter} target="_blank" rel="noopener noreferrer" className="social-link-brand social-x" data-testid="follow-x">
                      <Twitter size={20} />
                      <span>X</span>
                    </a>
                  </div>
                </div>
              </div>

              {optimizedResult.next_recipe_suggestions && optimizedResult.next_recipe_suggestions.length > 0 && (
                <div className="recipe-suggestions" style={{marginTop: '2rem'}}>
                  <p className="suggestions-label" style={{fontSize: '1.125rem', fontWeight: '700', marginBottom: '1rem'}}>Try these next:</p>
                  <div className="suggestion-tags">
                    {optimizedResult.next_recipe_suggestions.map((suggestion, idx) => (
                      <button 
                        key={idx}
                        className="suggestion-tag"
                        onClick={() => {
                          setRecipeText(suggestion);
                          setOptimizedResult(null);
                          window.scrollTo({ top: 0, behavior: 'smooth' });
                        }}
                        data-testid={`suggestion-${idx}`}
                      >
                        {suggestion}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </section>
      )}

      {/* Why the Swap Works - SOPHISTICATED VERSION */}
      <section id="why-different" className="why-swap-section">
        <div className="container">
          <div className="science-header">
            <span className="science-badge">BACKED BY NUTRITION SCIENCE</span>
            <h2 className="heading-1 section-title">The Science Behind the Switch</h2>
            <p className="body-large section-subtitle" style={{maxWidth: '700px', margin: '0 auto 2.5rem'}}>
              Most cooking oils are nutritionally empty. I Love Camelina<sup>®</sup> delivers what others can't — exceptional omega-3s with real heat stability.
            </p>
          </div>
          <div className="why-swap-grid">
            <div className="why-swap-card omega-card" data-testid="benefit-omega3">
              <div className="why-swap-icon omega-icon">5,000<span style={{fontSize: '0.5em'}}>mg</span></div>
              <h3 className="heading-3">Plant-Based Omega-3s</h3>
              <p className="body-medium">Per tablespoon — <strong>50x more than olive oil</strong>. Essential for heart health, brain function, and reducing inflammation. Your brain is 60% fat — feed it right.</p>
              <div className="card-fact">🧠 Supports cognitive function</div>
            </div>
            <div className="why-swap-card heat-card" data-testid="benefit-smoke-point">
              <div className="why-swap-icon heat-icon">475°<span style={{fontSize: '0.5em'}}>F</span></div>
              <h3 className="heading-3">High Heat Stability</h3>
              <p className="body-medium">Olive oil breaks down at 375°F releasing harmful compounds. <strong>Camelina stays stable 100°F higher.</strong> No refrigeration needed. 30+ month shelf life.</p>
              <div className="card-fact">🔥 Perfect for searing, frying, baking</div>
            </div>
            <div className="why-swap-card taste-card" data-testid="benefit-replacement">
              <div className="why-swap-icon taste-icon">💛</div>
              <h3 className="heading-3">Tastes Delicious</h3>
              <p className="body-medium"><strong>Virgin:</strong> Clean, light, neutral — won't overpower food.<br/><strong>Roasted:</strong> Rich sesame-like depth. Drizzle on anything — even ice cream tastes like Snickers!</p>
              <div className="card-fact">👨‍🍳 Chef-approved flavor</div>
            </div>
          </div>
        </div>
      </section>

      {/* Nutrition Comparison - VISUAL ADVANTAGE SHOWCASE */}
      <section className="nutrition-section">
        <div className="container">
          <div className="nutrition-header">
            <span className="advantage-badge">THE CAMELINA ADVANTAGE</span>
            <h2 className="heading-1 section-title">Why I Love Camelina<sup>®</sup> Wins</h2>
            <p className="body-large section-subtitle">One tablespoon tells the whole story</p>
          </div>
          
          {/* Visual Omega-3 Comparison */}
          <div className="advantage-showcase">
            {/* Omega-3 Visual */}
            <div className="advantage-card omega-advantage">
              <div className="advantage-icon">🧠</div>
              <h3>Omega-3s Per Tablespoon</h3>
              <div className="bar-comparison">
                <div className="bar-item camelina-bar">
                  <span className="bar-label">I Love Camelina<sup>®</sup></span>
                  <div className="bar-track">
                    <div className="bar-fill camelina-fill" style={{width: '100%'}}></div>
                  </div>
                  <span className="bar-value winner-value">5,000mg</span>
                </div>
                <div className="bar-item other-bar">
                  <span className="bar-label">Olive Oil</span>
                  <div className="bar-track">
                    <div className="bar-fill other-fill" style={{width: '2%'}}></div>
                  </div>
                  <span className="bar-value">100mg</span>
                </div>
                <div className="bar-item other-bar">
                  <span className="bar-label">Avocado Oil</span>
                  <div className="bar-track">
                    <div className="bar-fill other-fill" style={{width: '0%'}}></div>
                  </div>
                  <span className="bar-value">0mg</span>
                </div>
                <div className="bar-item other-bar">
                  <span className="bar-label">Beef Tallow</span>
                  <div className="bar-track">
                    <div className="bar-fill other-fill" style={{width: '1%'}}></div>
                  </div>
                  <span className="bar-value">50mg</span>
                </div>
              </div>
              <p className="advantage-callout">💛 50x more than olive • 100x more than tallow • actually plant-based</p>
            </div>
            
            {/* Smoke Point Visual */}
            <div className="advantage-card heat-advantage">
              <div className="advantage-icon">🔥</div>
              <h3>Heat Stability (Smoke Point)</h3>
              <div className="bar-comparison">
                <div className="bar-item camelina-bar">
                  <span className="bar-label">I Love Camelina<sup>®</sup></span>
                  <div className="bar-track heat-track">
                    <div className="bar-fill camelina-fill" style={{width: '95%'}}></div>
                  </div>
                  <span className="bar-value winner-value">475°F</span>
                </div>
                <div className="bar-item other-bar">
                  <span className="bar-label">Avocado Oil</span>
                  <div className="bar-track heat-track">
                    <div className="bar-fill other-fill" style={{width: '100%'}}></div>
                  </div>
                  <span className="bar-value">500°F</span>
                </div>
                <div className="bar-item other-bar">
                  <span className="bar-label">Beef Tallow</span>
                  <div className="bar-track heat-track">
                    <div className="bar-fill warning-fill" style={{width: '80%'}}></div>
                  </div>
                  <span className="bar-value">400°F</span>
                </div>
                <div className="bar-item other-bar">
                  <span className="bar-label">Olive Oil</span>
                  <div className="bar-track heat-track">
                    <div className="bar-fill warning-fill" style={{width: '75%'}}></div>
                  </div>
                  <span className="bar-value">375°F</span>
                </div>
              </div>
              <p className="advantage-callout">💛 100°F hotter than olive • won't break down like EVOO does</p>
            </div>
            
            {/* Saturated Fat Visual */}
            <div className="advantage-card sat-fat-advantage">
              <div className="advantage-icon">❤️</div>
              <h3>Saturated Fat (Less is Better)</h3>
              <div className="bar-comparison reverse-bars">
                <div className="bar-item camelina-bar">
                  <span className="bar-label">I Love Camelina<sup>®</sup></span>
                  <div className="bar-track">
                    <div className="bar-fill camelina-fill" style={{width: '8%'}}></div>
                  </div>
                  <span className="bar-value winner-value">1g</span>
                </div>
                <div className="bar-item other-bar">
                  <span className="bar-label">Olive Oil</span>
                  <div className="bar-track">
                    <div className="bar-fill neutral-fill" style={{width: '16%'}}></div>
                  </div>
                  <span className="bar-value">1.9g</span>
                </div>
                <div className="bar-item other-bar">
                  <span className="bar-label">Beef Tallow</span>
                  <div className="bar-track">
                    <div className="bar-fill warning-fill" style={{width: '50%'}}></div>
                  </div>
                  <span className="bar-value">6g</span>
                </div>
                <div className="bar-item other-bar">
                  <span className="bar-label">Coconut Oil</span>
                  <div className="bar-track">
                    <div className="bar-fill warning-fill" style={{width: '100%'}}></div>
                  </div>
                  <span className="bar-value">12g</span>
                </div>
              </div>
              <p className="advantage-callout">💛 6x less sat fat than tallow • Non-GMO Verified</p>
            </div>
          </div>
          
          {/* Why We're Different - Plant-Based Badges */}
          <div className="why-different-badges">
            <div className="badge-item">
              <img src={NON_GMO_LOGO} alt="Non-GMO Project Verified" className="nongmo-badge-img" />
            </div>
            <div className="badge-item">
              <span className="badge-emoji">🌱</span>
              <span>100% Plant-Based</span>
            </div>
            <div className="badge-item">
              <span className="badge-emoji">🇨🇦</span>
              <span>Made in Canada</span>
            </div>
            <div className="badge-item">
              <span className="badge-emoji">🔍</span>
              <span>Traceable Origin</span>
            </div>
          </div>
          
          {/* Bottom CTA */}
          <div className="nutrition-cta">
            <p className="cta-text">Ready to make the switch?</p>
            <a href="#recipe-optimizer" className="cta-button nutrition-cta-btn">
              Try the Recipe Optimizer
            </a>
          </div>
        </div>
      </section>

      {/* Flavor Profiles - Updated with official label text */}
      <section className="flavor-section">
        <div className="container">
          <h2 className="heading-1 section-title">Flavor Profiles</h2>
          <p className="body-large section-subtitle">The Omega-3 Oil You Will Love™</p>
          <div className="flavor-grid">
            <div className="flavor-card virgin-card">
              <img 
                src={PRODUCT_IMAGES.virgin}
                alt="Virgin Camelina Oil Camelina Oil"
                className="flavor-bottle"
              />
              <div className="product-badges">
                <img src={NON_GMO_LOGO} alt="Non-GMO" className="inline-nongmo-img" />
                <span className="badge badge-omega">Omega-3</span>
              </div>
              <h3 className="heading-2">Virgin Camelina Oil</h3>
              <p className="body-large flavor-description">
                Smooth, fresh and light with a clean, subtle nutty flavor. Perfectly balanced for sautéing, frying, marinades and dressings.
              </p>
              <p className="body-small flavor-usage">Cold Pressed • 16.9 fl oz (500 mL)</p>
              <a href={PRODUCT_URLS.virgin} target="_blank" rel="noopener noreferrer" className="btn-shop-blue" data-testid="buy-virgin-flavor">Buy Virgin</a>
            </div>
            <div className="flavor-card roasted-card">
              <img 
                src={PRODUCT_IMAGES.roasted}
                alt="Roasted Camelina Oil Virgin Camelina Oil"
                className="flavor-bottle"
              />
              <div className="product-badges">
                <img src={NON_GMO_LOGO} alt="Non-GMO" className="inline-nongmo-img" />
                <span className="badge badge-omega">Omega-3</span>
              </div>
              <h3 className="heading-2">Roasted Camelina Oil</h3>
              <p className="body-large flavor-description">
                Rich toasted and nutty flavor with hints of sesame and roasted peanut. A versatile virgin finishing oil for sautéing, dressings, marinades, and gourmet dishes.
              </p>
              <p className="body-small flavor-usage">Cold Pressed • 16.9 fl oz (500 mL)</p>
              <a href={PRODUCT_URLS.roasted} target="_blank" rel="noopener noreferrer" className="btn-shop-blue" data-testid="buy-roasted-flavor">Buy Roasted</a>
            </div>
          </div>
        </div>
      </section>

      {/* Farm & Heritage - "From Farm to Family" - With Animated Field */}
      <section className="farm-section">
        <img 
          src="https://customer-assets.emergentagent.com/job_recipeai-platform/artifacts/76l1eq7b_i-love-camelina-field.gif" 
          alt="Camelina field" 
          className="farm-bg-gif"
        />
        <div className="farm-overlay"></div>
        <div className="container">
          <div className="farm-content">
            <img 
              src={LOGO_URL} 
              alt="I Love Camelina<sup>®</sup>" 
              className="farm-logo" 
            />
            <h2 className="heading-1 farm-title">From Farm to Family</h2>
            <div className="farm-location-badge">
              <span className="canada-flag">🇨🇦</span>
              <span className="location-text">Sourced from Quebec, Canada</span>
            </div>
            <p className="farm-description">
              I Love Camelina<sup>®</sup> comes from Oliméga Inc., a sixth-generation family farm in St-Édouard, Quebec that has perfected camelina cultivation for decades.
            </p>
            <div className="farm-badges-row">
                <img src={NON_GMO_LOGO} alt="Non-GMO Project Verified" className="farm-nongmo-img" />
                <span className="farm-badge">🌱 Vegan</span>
                <span className="farm-badge">🥜 Peanut Free</span>
                <span className="farm-badge">✡️ Kosher</span>
              </div>
            <a 
              href="https://maps.app.goo.gl/yMTcnkLgpt7962CM7" 
              target="_blank" 
              rel="noopener noreferrer"
              className="farm-map-link"
            >
              <MapPin size={16} /> View Our Farm Location
            </a>
          </div>
        </div>
      </section>

      {/* History Section - Portrait image aligned with text */}
      <section className="history-section">
        <div className="container">
          <div className="history-content">
            <h2 className="heading-1 section-title">The History of Camelina</h2>
            <div className="history-grid">
              <div className="history-image">
                <img src={CAMELINA_FIELD_IMAGE} alt="Camelina field in bloom" className="history-field-img" />
              </div>
              <div className="history-text">
                <p className="body-large">
                  Camelina sativa, also known as &quot;gold of pleasure,&quot; was one of the earliest cultivated oilseed crops in human history.
                </p>
                <p className="body-medium">
                  Archaeological evidence shows camelina was grown in Northern Europe and Central Asia as far back as 7,000 BCE. It fueled ancient civilizations before being largely forgotten in the modern era.
                </p>
                <p className="body-medium">
                  Today, camelina is experiencing a renaissance as consumers discover its exceptional nutritional profile — particularly its omega-3 content, which rivals marine sources.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Camelina Cares - EXACT label language */}
      <section className="camelina-cares-section">
        <div className="container">
          <div className="cares-content">
            <div className="cares-badge">
              <div className="cares-logo-circle">
                <Heart size={28} className="cares-heart" />
                <Leaf size={20} className="cares-leaf" />
              </div>
              <h3 className="cares-title">Camelina Cares</h3>
            </div>
            <p className="cares-description">
              1% of our sales support brain and heart health through donations to Alzheimer&apos;s, dementia, and heart disease charities, clinical research, and sustainable agriculture initiatives.
            </p>
          </div>
        </div>
      </section>

      {/* Shop Section - Updated with official label text */}
      <section id="products" className="product-section">
        <div className="container">
          <h2 className="heading-1 section-title">Shop I Love Camelina<sup>®</sup></h2>
          <p className="body-large section-subtitle">SUSTAINABLE | NUTRITIOUS | DELICIOUS</p>
          
          <div className="product-grid">
            <div className="product-card virgin-card">
              <div className="product-image-container">
                <img 
                  src={PRODUCT_IMAGES.virgin}
                  alt="Virgin Camelina Oil"
                  className="product-image"
                />
              </div>
              <div className="product-badges">
                <img src={NON_GMO_LOGO} alt="Non-GMO" className="inline-nongmo-img" />
                <span className="badge badge-omega">Omega-3</span>
              </div>
              <h3 className="heading-3">Virgin Camelina Oil</h3>
              <p className="body-small product-size">Cold Pressed • 16.9 fl oz (500 mL)</p>
              <p className="body-medium product-description">Smooth, fresh and light with a clean, subtle nutty flavor. Perfectly balanced for sautéing, frying, marinades and dressings.</p>
              <a href={PRODUCT_URLS.virgin} target="_blank" rel="noopener noreferrer" className="btn-shop-blue product-cta" data-testid="buy-virgin-shop">
                <ShoppingCart size={18} />
                <span>Buy Virgin</span>
              </a>
            </div>

            <div className="product-card duo-card">
              <div className="featured-badge">Best Value</div>
              <div className="product-image-container">
                <img 
                  src={PRODUCT_IMAGES.duo}
                  alt="I Love Camelina<sup>®</sup> Duo - Virgin + Roasted Camelina Oil" 
                  className="product-image"
                />
              </div>
              <div className="product-badges">
                <img src={NON_GMO_LOGO} alt="Non-GMO" className="inline-nongmo-img" />
                <span className="badge badge-omega">Omega-3 Rich</span>
              </div>
              <h3 className="heading-3">Virgin + Roasted Duo</h3>
              <p className="body-small product-size">2 Bottles | 33.8 fl oz total • Cold Pressed</p>
              <p className="body-medium product-description">Complete collection. Virgin Camelina Oil for cooking, Roasted Camelina Oil for finishing. Two oils, endless possibilities.</p>
              {/* Shopify Bundler Widget */}
              <div className="bundler-target-element" data-bndlr-ccid="d3zu"></div>
              <a href={PRODUCT_URLS.duo} target="_blank" rel="noopener noreferrer" className="btn-shop-blue product-cta" data-testid="buy-duo-shop">
                <ShoppingCart size={18} />
                <span>Buy Duo Bundle</span>
              </a>
            </div>

            <div className="product-card roasted-card">
              <div className="product-image-container">
                <img 
                  src={PRODUCT_IMAGES.roasted}
                  alt="Roasted Camelina Oil"
                  className="product-image"
                />
              </div>
              <div className="product-badges">
                <img src={NON_GMO_LOGO} alt="Non-GMO" className="inline-nongmo-img" />
                <span className="badge badge-omega">Omega-3</span>
              </div>
              <h3 className="heading-3">Roasted Camelina Oil</h3>
              <p className="body-small product-size">Cold Pressed • 16.9 fl oz (500 mL)</p>
              <p className="body-medium product-description">Rich toasted and nutty flavor with hints of sesame and roasted peanut. A versatile virgin finishing oil for sautéing, dressings, marinades, and gourmet dishes.</p>
              <a href={PRODUCT_URLS.roasted} target="_blank" rel="noopener noreferrer" className="btn-shop-blue product-cta" data-testid="buy-roasted-shop">
                <ShoppingCart size={18} />
                <span>Buy Roasted</span>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Ask AI - Premium Search Experience */}
      <section className="discovery-section" id="ask-chatgpt">
        <div className="container">
          <div className="discovery-content">
            <div className="discovery-header">
              <span className="discovery-badge">VERIFIED BY AI</span>
              <h2 className="heading-hero discovery-title">Don't just take our word for it.</h2>
              <p className="body-large discovery-subtitle">Ask Google, ChatGPT, or any AI about camelina oil's benefits. We're confident in what you'll find:</p>
            </div>
            
            <div className="ai-chat-container" data-testid="ask-chatgpt-section">
              <div className="preset-questions">
                {presetQuestions.map((question, idx) => (
                  <button
                    key={idx}
                    className="preset-question-btn"
                    onClick={() => {
                      setChatQuestion(question);
                      handleAskChatGPT(question);
                    }}
                    data-testid={`preset-question-${idx}`}
                  >
                    "{question}"
                  </button>
                ))}
              </div>
              
              <div className="ai-search-wrapper">
                <div className="ai-search-label">
                  <Sparkles size={16} />
                  <span>Ask anything about cooking oils</span>
                </div>
                <div className="ai-chat-input-wrapper">
                  <input
                    type="text"
                    className="ai-chat-input"
                    placeholder="Type your question here..."
                    value={chatQuestion}
                    onChange={(e) => setChatQuestion(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && handleAskChatGPT()}
                    data-testid="chatgpt-input"
                  />
                  <button 
                    className="btn-learn ai-chat-submit"
                    onClick={() => handleAskChatGPT()}
                    disabled={isAskingChatGPT}
                    data-testid="chatgpt-submit"
                  >
                    {isAskingChatGPT ? <Loader2 size={18} className="spinning" /> : <><Send size={18} /> Ask AI</>}
                  </button>
                </div>
              </div>
              
              {chatAnswer && (
                <div className="ai-chat-response" data-testid="chatgpt-answer">
                  <div className="ai-avatar">
                    <MessageCircle size={24} />
                  </div>
                  <div className="ai-message">
                    <p>{chatAnswer}</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Oil Poll Section - PROMINENT POLL DESIGN */}
      <section className="oil-poll-section" id="oil-poll" data-testid="oil-poll-section">
        <div className="container">
          <div className="poll-wrapper">
            {/* Logo badge */}
            <div className="poll-logo-badge">
              <img src={LOGO_URL} alt="I Love Camelina" className="poll-logo" />
            </div>
            
            <div className="poll-content">
              {pollStep === 0 && (
                <div className="poll-intro">
                  <div className="poll-header-badge">
                    <span className="poll-tag">TAKE THE POLL</span>
                  </div>
                  <h2 className="heading-1 poll-title">What Cooking Oils Do You Use?</h2>
                  <p className="body-large poll-subtitle">
                    Tell us what's in your kitchen — we'll show you how to upgrade your nutrition. Plus get <strong>10% off</strong> your first order!
                  </p>
                  <button 
                    className="btn-poll-start"
                    onClick={() => setPollStep(1)}
                    data-testid="poll-start-btn"
                  >
                    <Sparkles size={20} />
                    <span>Start the Poll</span>
                  </button>
                </div>
              )}

              {pollStep === 1 && (
                <div className="poll-oil-selection">
                  <h2 className="heading-2">Select ALL the oils you use:</h2>
                  <p className="body-medium poll-instruction">(Select as many as apply)</p>
                  <div className="oil-options-grid">
                    {OIL_TYPES.map((oil) => (
                      <button
                        key={oil}
                        className={`oil-option-btn ${pollData.selectedOils.includes(oil) ? 'selected' : ''}`}
                        onClick={() => handleOilToggle(oil)}
                        data-testid={`poll-oil-${oil.toLowerCase().replace(/\s+/g, '-')}`}
                      >
                        <CheckCircle size={16} className={`check-icon ${pollData.selectedOils.includes(oil) ? 'visible' : ''}`} />
                        {oil}
                      </button>
                    ))}
                  </div>
                  <button 
                    className="btn-poll-next"
                    onClick={handleOilsNext}
                    disabled={pollData.selectedOils.length === 0}
                  >
                    Continue →
                  </button>
                </div>
              )}

              {pollStep === 2 && (
                <div className="poll-brand-selection">
                  <h2 className="heading-2">Which brands do you buy?</h2>
                  <p className="body-medium poll-instruction">Select brands for each oil type (optional):</p>
                  
                  <div className="brand-selection-container">
                    {pollData.selectedOils.map((oil) => (
                      <div key={oil} className="brand-group">
                        <h4 className="brand-group-title">{oil}</h4>
                        <div className="brand-options">
                          {OIL_BRANDS[oil]?.map((brand) => (
                            <button
                              key={brand}
                              className={`brand-option-btn ${pollData.selectedBrands[oil]?.includes(brand) ? 'selected' : ''}`}
                              onClick={() => handleBrandToggle(oil, brand)}
                            >
                              {brand}
                            </button>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                  
                  <div className="poll-nav-buttons">
                    <button className="btn-text-link" onClick={() => setPollStep(1)}>← Back</button>
                    <button className="btn-poll-next" onClick={handleBrandsNext}>Continue →</button>
                  </div>
                </div>
              )}

              {pollStep === 3 && (
                <div className="poll-contact-form">
                  <h2 className="heading-2">Almost done!</h2>
                  <p className="body-medium">Enter your info to see your personalized results + get your 10% discount:</p>
                  
                  <form onSubmit={handlePollSubmit} className="poll-form">
                    <div className="form-row">
                      <input
                        type="text"
                        className="form-input"
                        placeholder="First name *"
                        value={pollData.firstName}
                        onChange={(e) => setPollData(prev => ({ ...prev, firstName: e.target.value }))}
                        required
                        data-testid="poll-firstname"
                      />
                    </div>
                    <div className="form-row">
                      <input
                        type="email"
                        className="form-input"
                        placeholder="Email *"
                        value={pollData.email}
                        onChange={(e) => setPollData(prev => ({ ...prev, email: e.target.value }))}
                        required
                        data-testid="poll-email"
                      />
                    </div>
                    <div className="form-row">
                      <input
                        type="tel"
                        className="form-input"
                        placeholder="Phone (optional)"
                        value={pollData.phone}
                        onChange={(e) => setPollData(prev => ({ ...prev, phone: e.target.value }))}
                        data-testid="poll-phone"
                      />
                    </div>
                    <button 
                      type="submit" 
                      className="btn-poll-next poll-submit-btn"
                      disabled={isSubmittingPoll}
                      data-testid="poll-submit"
                    >
                      {isSubmittingPoll ? (
                        <><Loader2 size={18} className="spinning" /> Analyzing...</>
                      ) : (
                        <><Sparkles size={18} /> See My Results</>
                      )}
                    </button>
                  </form>
                  
                  <button className="btn-text-link" onClick={() => setPollStep(2)}>← Back</button>
                </div>
              )}

              {pollStep === 4 && pollResult && (
                <div className="poll-results" data-testid="poll-results">
                  {/* Header with Logo */}
                  <div className="poll-result-header">
                    <img src={LOGO_URL} alt="I Love Camelina<sup>®</sup>" className="results-logo" />
                    <h2 className="heading-2">Thanks {pollData.firstName}! Here's Your Personalized Oil Analysis</h2>
                    <p className="results-subtitle">We analyzed {pollData.selectedOils.length} oil{pollData.selectedOils.length > 1 ? 's' : ''} you currently use</p>
                  </div>
                  
                  {/* Dynamic Oil Comparison Cards */}
                  {pollResult.comparisons && pollResult.comparisons.length > 0 && (
                    <div className="oil-comparison-cards">
                      <h3 className="comparison-section-title">Your Oils vs. I Love Camelina<sup>®</sup></h3>
                      {pollResult.comparisons.map((comparison, index) => (
                        <div key={index} className="oil-comparison-card" data-testid={`comparison-card-${index}`}>
                          <div className="comparison-card-header">
                            <span className="oil-badge">{comparison.oil_name}</span>
                            <span className="vs-badge">vs</span>
                            <span className="camelina-badge">💛 Camelina</span>
                          </div>
                          
                          <div className="comparison-stats-grid">
                            <div className="stat-comparison">
                              <span className="stat-label">Omega-3s</span>
                              <div className="stat-values">
                                <span className="their-value">{comparison.omega3_mg}</span>
                                <span className="arrow">→</span>
                                <span className="our-value">~5,000mg/tbsp</span>
                              </div>
                            </div>
                            <div className="stat-comparison">
                              <span className="stat-label">Smoke Point</span>
                              <div className="stat-values">
                                <span className="their-value">{comparison.smoke_point}</span>
                                <span className="arrow">→</span>
                                <span className="our-value">475°F</span>
                              </div>
                            </div>
                            <div className="stat-comparison">
                              <span className="stat-label">Sat. Fat</span>
                              <div className="stat-values">
                                <span className="their-value">{comparison.saturated_fat}</span>
                                <span className="arrow">→</span>
                                <span className="our-value">~1g/tbsp</span>
                              </div>
                            </div>
                          </div>
                          
                          <div className="comparison-analysis">
                            <div className="weakness-section">
                              <span className="analysis-label">⚠️ The Issue</span>
                              <p>{comparison.key_weakness}</p>
                            </div>
                            <div className="advantage-section">
                              <span className="analysis-label">💛 Why I Love Camelina<sup>®</sup></span>
                              <p>{comparison.camelina_advantage}</p>
                            </div>
                          </div>
                          
                          {/* Brand-Specific Callouts */}
                          {comparison.brand_callouts && comparison.brand_callouts.length > 0 && (
                            <div className="brand-callouts">
                              <span className="brand-callout-label">📊 Brand Analysis</span>
                              {comparison.brand_callouts.map((callout, i) => (
                                <p key={i} className="brand-callout-text" dangerouslySetInnerHTML={{__html: callout.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')}} />
                              ))}
                            </div>
                          )}
                        </div>
                      ))}
                      
                      {/* Gen Z Recap - Dynamic based on selected oils */}
                      <div className="genz-recap" data-testid="genz-recap">
                        <div className="recap-bubble">
                          <span className="recap-emoji">💭</span>
                          <div className="recap-text">
                            <p className="recap-inner-monologue">*me looking at my oil cabinet*</p>
                            <p className="recap-thought">
                              {pollData.selectedOils?.includes('Olive Oil') ? (
                                <>ok so my olive oil has like 100mg omega-3s and starts smoking at 375°F... meanwhile I Love Camelina<sup>®</sup> has 50x more omega-3s AND handles 475°F?? the math is mathing 📊</>
                              ) : pollData.selectedOils?.includes('Avocado Oil') ? (
                                <>wait so avocado oil literally has ZERO omega-3s and 82% of them are fake?? I Love Camelina<sup>®</sup> is giving 5,000mg omega-3s with similar heat tolerance but actually verified... not the plot twist I expected 💀</>
                              ) : pollData.selectedOils?.includes('Coconut Oil') ? (
                                <>coconut oil is 12g saturated fat per tbsp?? that's more than a Big Mac 🍔 I Love Camelina<sup>®</sup> has 1g sat fat + 5,000mg omega-3s... choosing heart health tbh 💛</>
                              ) : pollData.selectedOils?.includes('Beef Tallow') ? (
                                <>ok tallow is having a moment rn but 6g saturated fat and zero omega-3s? I Love Camelina<sup>®</sup> is plant-based with 5,000mg omega-3s AND higher smoke point... it's giving same energy without the cholesterol 💛</>
                              ) : pollData.selectedOils?.includes('Vegetable Oil') ? (
                                <>vegetable oil is basically mystery sludge extracted with chemicals?? I Love Camelina<sup>®</sup> is cold-pressed, single-origin, 5,000mg omega-3s from a family farm... adulting is choosing quality ngl 💛</>
                              ) : (
                                <>ok so my {pollData.selectedOils[0]?.toLowerCase() || 'oil'} is basically giving nothing nutritionally... meanwhile I Love Camelina<sup>®</sup> is out here with 5,000mg omega-3s AND it actually tastes good?? like a subtle nutty clean flavor that doesn't mess with my food??</>
                              )}
                            </p>
                            <p className="recap-conclusion">the way this is an actual upgrade and not just health marketing... I might actually say "I love camelina" out loud ngl 💛</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                  
                  {/* Camelina Facts Bar */}
                  <div className="camelina-facts-bar">
                    <div className="fact-item">
                      <span className="fact-number">5,000mg</span>
                      <span className="fact-label">Omega-3s per tbsp</span>
                    </div>
                    <div className="fact-item">
                      <span className="fact-number">475°F</span>
                      <span className="fact-label">Smoke Point</span>
                    </div>
                    <div className="fact-item">
                      <span className="fact-number">1:1</span>
                      <span className="fact-label">Swap Ratio</span>
                    </div>
                    <div className="fact-item">
                      <span className="fact-number">6th Gen</span>
                      <span className="fact-label">Family Farm</span>
                    </div>
                  </div>
                  
                  {/* Health Education Cards */}
                  <div className="health-education-section">
                    <h3 className="education-title">Why Omega-3s Matter for Your Health</h3>
                    <div className="health-cards-grid">
                      <div className="health-card">
                        <div className="health-card-icon brain-icon">🧠</div>
                        <h4>Brain Health</h4>
                        <p>Omega-3s support cognitive function in adults and seniors. Studies suggest ALA may help maintain healthy brain aging. For children, omega-3s support learning and development.</p>
                      </div>
                      <div className="health-card">
                        <div className="health-card-icon heart-icon">❤️</div>
                        <h4>Heart Health</h4>
                        <p>Backed by peer-reviewed nutrition research. Camelina oil is studied for cardiovascular and metabolic health. Naturally rich in ALA omega-3s, shown to support heart health.</p>
                      </div>
                      <div className="health-card">
                        <div className="health-card-icon inflammation-icon">🔥</div>
                        <h4>Inflammation Support</h4>
                        <p>Do you have joint pain, skin concerns like rosacea, gut issues, or recovering from injuries? Omega-3s are studied for their role in supporting the body's natural inflammatory response.</p>
                      </div>
                    </div>
                  </div>
                  
                  {/* Delicious Ways to Use */}
                  <div className="usage-ideas-section">
                    <h3 className="usage-title">Delicious Ways to Get 10,000-20,000mg Daily</h3>
                    <div className="usage-grid">
                      <div className="usage-idea">
                        <span className="usage-icon">🥤</span>
                        <span className="usage-text">Add 1 tbsp to your morning smoothie</span>
                      </div>
                      <div className="usage-idea">
                        <span className="usage-icon">🍳</span>
                        <span className="usage-text">Use instead of butter for scrambled or fried eggs</span>
                      </div>
                      <div className="usage-idea">
                        <span className="usage-icon">🥗</span>
                        <span className="usage-text">Make a delicious dressing with apple cider vinegar, turmeric, pepper, shallots & garlic</span>
                      </div>
                      <div className="usage-idea">
                        <span className="usage-icon">🥦</span>
                        <span className="usage-text">Sauté vegetables for a delicious, healthy side</span>
                      </div>
                      <div className="usage-idea">
                        <span className="usage-icon">🍖</span>
                        <span className="usage-text">Use as a marinade or for cooking proteins</span>
                      </div>
                      <div className="usage-idea roasted-special">
                        <span className="usage-icon">🍨</span>
                        <span className="usage-text"><strong>Roasted:</strong> Drizzle on ice cream or yogurt with berries — tastes like Snickers! Nutty flavor without nut allergy concerns.</span>
                      </div>
                    </div>
                  </div>
                  
                  {/* Flavor Profiles */}
                  <div className="flavor-education">
                    <div className="flavor-card-result virgin-flavor">
                      <img src={BOTTLE_IMAGES.virgin} alt="Virgin Camelina Oil" className="flavor-bottle-img" />
                      <div className="flavor-info">
                        <h4>Virgin Camelina Oil</h4>
                        <p className="flavor-profile">Mild, neutral, clean tasting — won't overpower food. Light notes of asparagus and snap peas. Perfect for everyday cooking.</p>
                        <p className="flavor-uses">Perfect for: frying, sautéing, grilling, baking, stir-frying, air frying</p>
                      </div>
                    </div>
                    <div className="flavor-card-result roasted-flavor">
                      <img src={BOTTLE_IMAGES.roasted} alt="Roasted Camelina Oil" className="flavor-bottle-img" />
                      <div className="flavor-info">
                        <h4>Roasted Camelina Oil</h4>
                        <p className="flavor-profile">Richly fragrant and nutty, with notes of toasted sesame and roasted peanut. A chef's secret for layering depth and aroma.</p>
                        <p className="flavor-uses">Perfect for: drizzling over rice, roasted vegetables, hummus, noodles, stir-fries, grain bowls, popcorn, even ice cream!</p>
                      </div>
                    </div>
                  </div>
                  
                  {/* 🏆 THE BETTER OIL CHALLENGE */}
                  <div className="better-oil-challenge">
                    <div className="challenge-header">
                      <span className="challenge-badge">🏆 THE BETTER OIL CHALLENGE 🏆</span>
                      <h3 className="challenge-title">Use Camelina for 30 Days</h3>
                    </div>
                    <p className="challenge-description">
                      Sauté, roast, bake, add to smoothies, or drizzle on your favorite proteins. 
                      See how it fits your lifestyle. <strong>Feel how it fits your body.</strong>
                    </p>
                    <p className="challenge-quote">
                      "Camelina isn't just better. It's the upgrade your oil aisle never offered."
                    </p>
                  </div>
                  
                  {/* PREMIUM COUPON CARD WITH BOTTLES */}
                  <div className="coupon-card-premium">
                    <div className="coupon-gradient-border">
                      <div className="coupon-header">
                        <img src={LOGO_URL} alt="I Love Camelina®" className="coupon-header-logo" />
                      </div>
                      <div className="coupon-body">
                        <div className="coupon-bottles-preview">
                          <img src={BOTTLE_IMAGES.virgin} alt="Virgin" className="coupon-bottle" />
                          <img src={BOTTLE_IMAGES.roasted} alt="Roasted" className="coupon-bottle" />
                        </div>
                        <p className="coupon-label">YOUR EXCLUSIVE OFFER</p>
                        <div className="coupon-amount">
                          <span className="coupon-percent">10%</span>
                          <span className="coupon-off">OFF YOUR FIRST ORDER</span>
                        </div>
                        <div className="coupon-code-container">
                          <span className="coupon-code-label">USE CODE:</span>
                          <span className="coupon-code-value">{pollResult.discount_code}</span>
                        </div>
                        <p className="coupon-validity">Valid at ILoveCamelina.com • No minimum purchase</p>
                      </div>
                      <a 
                        href={PRODUCT_URLS.buyNow} 
                        target="_blank" 
                        rel="noopener noreferrer" 
                        className="coupon-cta-btn"
                        data-testid="poll-shop-btn"
                      >
                        <ShoppingCart size={20} />
                        <span>SHOP NOW →</span>
                      </a>
                    </div>
                  </div>
                  
                  {/* NUTRITION FACTS PANEL */}
                  <div className="nutrition-facts-panel">
                    <h4 className="nutrition-title">Nutrition Facts</h4>
                    <p className="nutrition-serving">Per 1 tbsp (15ml / 14g)</p>
                    <div className="nutrition-grid">
                      <div className="nutrition-row">
                        <span className="nutrition-label">Calories</span>
                        <span className="nutrition-value">120</span>
                      </div>
                      <div className="nutrition-row">
                        <span className="nutrition-label">Total Fat</span>
                        <span className="nutrition-value">14g (22% DV)</span>
                      </div>
                      <div className="nutrition-row highlight-row">
                        <span className="nutrition-label">→ Omega-3 (ALA)</span>
                        <span className="nutrition-value omega-highlight">5,000mg</span>
                      </div>
                      <div className="nutrition-row">
                        <span className="nutrition-label">→ Omega-6</span>
                        <span className="nutrition-value">3g</span>
                      </div>
                      <div className="nutrition-row">
                        <span className="nutrition-label">Vitamin E</span>
                        <span className="nutrition-value">1.13mg (8% DV)</span>
                      </div>
                    </div>
                    <p className="nutrition-ingredient">Ingredients: 100% Cold-Pressed Camelina Oil</p>
                  </div>
                  
                  {/* CERTIFICATIONS */}
                  <div className="certifications-bar">
                    <p className="certifications-label">CERTIFIED & TRUSTED</p>
                    <div className="certifications-icons">
                      <div className="cert-item">
                        <img src={NON_GMO_LOGO} alt="Non-GMO" className="cert-nongmo-img" />
                      </div>
                      <div className="cert-item">
                        <span className="cert-icon">🌱</span>
                        <span className="cert-name">Vegan</span>
                      </div>
                      <div className="cert-item">
                        <span className="cert-icon">🥜</span>
                        <span className="cert-name">Peanut-Free</span>
                      </div>
                      <div className="cert-item">
                        <span className="cert-icon">🇨🇦</span>
                        <span className="cert-name">Made in Canada</span>
                      </div>
                    </div>
                  </div>
                  
                  {/* 💚 CAMELINA CARES */}
                  <div className="camelina-cares-box">
                    <p className="cares-text">
                      💚 <strong>Camelina Cares:</strong> 1% of every sale supports brain & heart health research — 
                      Alzheimer's, dementia, and heart disease charities.
                    </p>
                  </div>
                  
                  {/* Social Sharing */}
                  <div className="share-results-section">
                    <h4 className="share-title">Share Your Results</h4>
                    <p className="share-subtitle">Help others discover the healthiest cooking oil</p>
                    <div className="share-buttons">
                      <button 
                        className="share-btn share-email"
                        onClick={() => handleShareResults('email')}
                        data-testid="share-email"
                      >
                        <Mail size={18} />
                        <span>Email Results</span>
                      </button>
                      <button 
                        className="share-btn share-facebook"
                        onClick={() => handleShareResults('facebook')}
                        data-testid="share-facebook"
                      >
                        <Facebook size={18} />
                        <span>Share</span>
                      </button>
                      <button 
                        className="share-btn share-twitter"
                        onClick={() => handleShareResults('twitter')}
                        data-testid="share-twitter"
                      >
                        <Twitter size={18} />
                        <span>Post</span>
                      </button>
                      <button 
                        className="share-btn share-linkedin"
                        onClick={() => handleShareResults('linkedin')}
                        data-testid="share-linkedin"
                      >
                        <Linkedin size={18} />
                        <span>Share</span>
                      </button>
                      <button 
                        className="share-btn share-copy"
                        onClick={() => handleShareResults('copy')}
                        data-testid="share-copy"
                      >
                        <Share2 size={18} />
                        <span>Copy Link</span>
                      </button>
                    </div>
                    <p className="share-note">Links shared without coupon code to maximize reach</p>
                  </div>
                  
                  <button 
                    className="btn-text-link"
                    onClick={() => {
                      setPollStep(0);
                      setPollData({ firstName: '', email: '', phone: '', selectedOils: [], selectedBrands: {} });
                      setPollResult(null);
                    }}
                  >
                    Take poll again
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Social Proof Section - with camelina field background */}
      <section className="social-proof-section" style={{ backgroundImage: `linear-gradient(rgba(255,255,255,0.85), rgba(255,255,255,0.85)), url(${CAMELINA_FIELD_IMAGE})`, backgroundSize: 'cover', backgroundPosition: 'center' }}>
        <div className="container">
          <h3 className="heading-3">See what people are cooking with I Love Camelina<sup>®</sup></h3>
          <div className="social-links-row">
            <a href={SOCIAL_LINKS.instagram} target="_blank" rel="noopener noreferrer" className="social-link-instagram" data-testid="social-instagram">
              <Instagram size={28} />
              <span>@ilovecamelina</span>
            </a>
            <a href={SOCIAL_LINKS.tiktok} target="_blank" rel="noopener noreferrer" className="social-link-tiktok" data-testid="social-tiktok">
              <Coffee size={28} />
              <span>@ilovecamelina</span>
            </a>
            <a href={SOCIAL_LINKS.twitter} target="_blank" rel="noopener noreferrer" className="social-link-twitter" data-testid="social-twitter">
              <Twitter size={28} />
              <span>@ilovecamelina</span>
            </a>
            <a href={SOCIAL_LINKS.linkedin} target="_blank" rel="noopener noreferrer" className="social-link-linkedin" data-testid="social-linkedin">
              <Linkedin size={28} />
              <span>I Love Camelina</span>
            </a>
          </div>
        </div>
      </section>

      {/* Final Purchase CTA - With products and food images */}
      <section className="final-cta-section">
        <div className="final-cta-bg"></div>
        <div className="container">
          <div className="final-cta-content">
            <div className="final-cta-images">
              <img 
                src={BOTTLE_IMAGES.virgin} 
                alt="Virgin Camelina Oil" 
                className="final-cta-bottle"
              />
              <img 
                src={BOTTLE_IMAGES.roasted} 
                alt="Roasted Camelina Oil" 
                className="final-cta-bottle"
              />
            </div>
            <div className="final-cta-text">
              <h2 className="heading-1">Ready to Upgrade Your Cooking?</h2>
              <p className="body-large">Start with I Love Camelina<sup>®</sup>. You'll taste the difference.</p>
              <div className="final-cta-features">
                <span className="final-feature"><Heart size={16} /> 5,000mg Omega-3s</span>
                <span className="final-feature"><Flame size={16} /> 475°F Smoke Point</span>
                <span className="final-feature"><CheckCircle size={16} /> 1:1 Replacement</span>
              </div>
              <a 
                href={PRODUCT_URLS.buyNow}
                target="_blank"
                rel="noopener noreferrer"
                className="btn-shop-blue btn-large"
                data-testid="final-cta-shop"
              >
                <ShoppingCart size={20} />
                <span>Shop Now</span>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Footer - with green flowing ribbon pattern EXACTLY matching label */}
      <footer className="camelina-footer">
        {/* 3-Layer Green Ribbon Pattern + Solid Bar - matching label design */}
        <div className="footer-ribbon-container">
          <svg viewBox="0 0 1440 280" preserveAspectRatio="none" xmlns="http://www.w3.org/2000/svg">
            {/* Solid dark green bar at very bottom */}
            <rect x="0" y="250" width="1440" height="30" fill="#1a4d0f"/>
            {/* Layer 1: Darkest green ribbon (top flowing ribbon) */}
            <path d="M0,280 L0,120 Q200,180 400,100 Q600,40 800,90 Q1000,150 1200,80 Q1400,20 1440,60 L1440,280 Z" fill="#2d6a1e"/>
            {/* Layer 2: Medium green ribbon */}
            <path d="M0,280 L0,160 Q250,200 500,140 Q750,90 1000,150 Q1250,190 1440,120 L1440,280 Z" fill="#4a9a2a"/>
            {/* Layer 3: Light/bright green ribbon */}
            <path d="M0,280 L0,200 Q300,230 600,180 Q900,140 1200,190 Q1350,210 1440,170 L1440,280 Z" fill="#6db835"/>
            {/* Layer 4: Lightest green ribbon closest to solid bar */}
            <path d="M0,280 L0,230 Q360,250 720,220 Q1080,195 1440,230 L1440,280 Z" fill="#84C441"/>
          </svg>
        </div>
        <div className="container">
          <div className="footer-content">
            <div className="footer-brand">
              <img 
                src={LOGO_URL} 
                alt="I Love Camelina<sup>®</sup>" 
                className="footer-logo" 
              />
              <p className="footer-tagline">The Omega-3 Oil You Will Love™</p>
              <div className="footer-badges">
                <span className="footer-badge">Non-GMO Verified</span>
                <span className="footer-badge">Plant Based</span>
                <span className="footer-badge">Cold Pressed</span>
              </div>
            </div>
            <div className="footer-links">
              <div className="footer-column">
                <h4 className="footer-heading">Shop</h4>
                <a href={PRODUCT_URLS.virgin} target="_blank" rel="noopener noreferrer" className="footer-link">Virgin Camelina Oil</a>
                <a href={PRODUCT_URLS.roasted} target="_blank" rel="noopener noreferrer" className="footer-link">Roasted Camelina Oil</a>
                <a href={PRODUCT_URLS.buyNow} target="_blank" rel="noopener noreferrer" className="footer-link">All Products</a>
              </div>
              <div className="footer-column">
                <h4 className="footer-heading">Learn</h4>
                <a href={FOOTER_URLS.aboutUs} target="_blank" rel="noopener noreferrer" className="footer-link">About Us</a>
                <a href={FOOTER_URLS.research} target="_blank" rel="noopener noreferrer" className="footer-link">Research</a>
                <a href={FOOTER_URLS.nutrition} target="_blank" rel="noopener noreferrer" className="footer-link">Nutrition</a>
              </div>
              <div className="footer-column">
                <h4 className="footer-heading">Follow</h4>
                <a href={SOCIAL_LINKS.instagram} target="_blank" rel="noopener noreferrer" className="footer-link">Instagram</a>
                <a href={SOCIAL_LINKS.tiktok} target="_blank" rel="noopener noreferrer" className="footer-link">TikTok</a>
                <a href={SOCIAL_LINKS.twitter} target="_blank" rel="noopener noreferrer" className="footer-link">X (Twitter)</a>
                <a href={SOCIAL_LINKS.linkedin} target="_blank" rel="noopener noreferrer" className="footer-link">LinkedIn</a>
              </div>
              <div className="footer-column">
                <h4 className="footer-heading">Company</h4>
                <a href={FOOTER_URLS.contact} target="_blank" rel="noopener noreferrer" className="footer-link">Contact Us</a>
                <a href={FOOTER_URLS.privacy} target="_blank" rel="noopener noreferrer" className="footer-link">Privacy Policy</a>
                <a href={FOOTER_URLS.terms} target="_blank" rel="noopener noreferrer" className="footer-link">Terms of Service</a>
              </div>
            </div>
          </div>
          <div className="footer-disclaimer">
            <p className="footer-caption">
              *Nutritional estimates for educational purposes. Statements not evaluated by FDA. Not intended to diagnose, treat, cure, or prevent disease.
            </p>
            <p className="footer-caption copyright">
              © 2025 I Love Camelina<sup>®</sup>. All rights reserved.
            </p>
          </div>
        </div>
      </footer>

      {/* Email Capture Modal */}
      {showEmailCapture && (
        <div className="capture-modal-overlay" onClick={() => setShowEmailCapture(false)} data-testid="email-modal">
          <div className="capture-modal" onClick={(e) => e.stopPropagation()}>
            <button className="modal-close" onClick={() => setShowEmailCapture(false)} data-testid="email-modal-close">
              <X size={24} />
            </button>
            
            {/* Logo */}
            <div className="modal-logo">
              <img 
                src="https://www.ilovecamelina.com/cdn/shop/files/I-LOVE-CAMELINA-CHOCOLATE-CHIP-COOkies-Omega-3_200x.png?v=1743285588" 
                alt="I Love Camelina" 
                style={{height: '50px', marginBottom: '15px'}}
              />
            </div>
            
            <div className="modal-icon">
              <Mail size={40} />
            </div>
            <h3 className="heading-2">Get Your Recipe 💛</h3>
            <p className="body-medium">We'll send your upgraded recipe + a lil 10% off code. No spam, we promise.</p>
            
            <form onSubmit={handleEmailSubmit} className="capture-form">
              <input
                type="email"
                className="form-input"
                placeholder="your@email.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                data-testid="email-input"
              />
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={emailOptIn}
                  onChange={(e) => setEmailOptIn(e.target.checked)}
                />
                <span className="body-small">Send me cooking tips & exclusive drops</span>
              </label>
              <button 
                type="submit" 
                className="btn-buy btn-large" 
                disabled={isSubmittingEmail}
                data-testid="email-submit"
              >
                {isSubmittingEmail ? (
                  <><Loader2 size={18} className="spinning" /> Sending...</>
                ) : (
                  <><Mail size={18} /> Send My Recipe</>
                )}
              </button>
            </form>
            <p className="body-small privacy-note" style={{marginTop: '12px', color: '#888', fontSize: '11px'}}>
              By signing up, you agree to our{' '}
              <a href="https://www.ilovecamelina.com/pages/terms" target="_blank" rel="noopener noreferrer" style={{color: '#2d5a27'}}>Terms</a>
              {' & '}
              <a href="https://www.ilovecamelina.com/pages/privacy" target="_blank" rel="noopener noreferrer" style={{color: '#2d5a27'}}>Privacy Policy</a>
              . Unsubscribe anytime.
            </p>
            <button className="btn-text-link" onClick={() => setShowEmailCapture(false)}>Maybe later</button>
          </div>
        </div>
      )}

      {/* SMS Capture Modal */}
      {showSmsCapture && (
        <div className="capture-modal-overlay" onClick={() => setShowSmsCapture(false)} data-testid="sms-modal">
          <div className="capture-modal" onClick={(e) => e.stopPropagation()}>
            <button className="modal-close" onClick={() => setShowSmsCapture(false)} data-testid="sms-modal-close">
              <X size={24} />
            </button>
            
            {/* Logo */}
            <div className="modal-logo">
              <img 
                src="https://www.ilovecamelina.com/cdn/shop/files/I-LOVE-CAMELINA-CHOCOLATE-CHIP-COOkies-Omega-3_200x.png?v=1743285588" 
                alt="I Love Camelina" 
                style={{height: '50px', marginBottom: '15px'}}
              />
            </div>
            
            <div className="modal-icon success">
              <CheckCircle size={40} />
            </div>
            <h3 className="heading-2">Recipe Sent! 💛</h3>
            <p className="body-medium">Check your inbox bestie. Want 10% off your first order?</p>
            
            <form onSubmit={handleSmsSubmit} className="capture-form">
              <input
                type="tel"
                className="form-input"
                placeholder="Your digits (optional)"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                data-testid="phone-input"
              />
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={smsOptIn}
                  onChange={(e) => setSmsOptIn(e.target.checked)}
                />
                <span className="body-small">Yeah, text me the good stuff (max 4/month, no cap)</span>
              </label>
              <button 
                type="submit" 
                className="btn-learn btn-large" 
                disabled={isSubmittingSms}
                data-testid="sms-submit"
              >
                {isSubmittingSms ? (
                  <><Loader2 size={18} className="spinning" /> Sending...</>
                ) : phone ? (
                  <><Phone size={18} /> Send My 10% Code</>
                ) : (
                  <>Skip for Now</>
                )}
              </button>
            </form>
            <p className="body-small privacy-note" style={{marginTop: '12px', color: '#888', fontSize: '11px'}}>
              By signing up, you agree to our{' '}
              <a href="https://www.ilovecamelina.com/pages/terms" target="_blank" rel="noopener noreferrer" style={{color: '#2d5a27'}}>Terms</a>
              {' & '}
              <a href="https://www.ilovecamelina.com/pages/privacy" target="_blank" rel="noopener noreferrer" style={{color: '#2d5a27'}}>Privacy Policy</a>
              . Reply STOP to unsubscribe anytime. Msg & data rates may apply.
            </p>
          </div>
        </div>
      )}

      {/* Share Modal */}
      {showShareModal && (
        <div className="capture-modal-overlay" onClick={() => setShowShareModal(false)} data-testid="share-modal">
          <div className="capture-modal share-modal" onClick={(e) => e.stopPropagation()}>
            <button className="modal-close" onClick={() => setShowShareModal(false)}>
              <X size={24} />
            </button>
            <h3 className="heading-2">Share Your Recipe</h3>
            <p className="body-medium">Spread the word about healthier cooking!</p>
            <div className="share-buttons">
              <button className="share-btn facebook" onClick={() => handleShare('facebook')} data-testid="share-facebook">
                <Facebook size={24} />
                <span>Facebook</span>
              </button>
              <button className="share-btn twitter" onClick={() => handleShare('twitter')} data-testid="share-twitter">
                <Twitter size={24} />
                <span>X / Twitter</span>
              </button>
              <button className="share-btn linkedin" onClick={() => handleShare('linkedin')} data-testid="share-linkedin">
                <Linkedin size={24} />
                <span>LinkedIn</span>
              </button>
              <button className="share-btn instagram" onClick={() => handleShare('instagram')} data-testid="share-instagram">
                <Instagram size={24} />
                <span>Instagram</span>
              </button>
            </div>
            <button className="btn-text-link" onClick={() => setShowShareModal(false)}>Close</button>
          </div>
        </div>
      )}
    </div>
  );
};

export default Home;
